import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { createRequestCache } from '../lib/requestCache';
import { DEFAULT_TENANT_ID } from '../lib/defaultTenant';
import { Customer, Location, Contact, EmailTemplate, WhatsAppTemplate, Booking as CrmBooking } from '../types/crm';
import {
  deferBookingWriteToOfflineIfNeeded,
  deferContactWriteToOfflineIfNeeded,
  deferCustomerWriteToOfflineIfNeeded,
  deferLocationWriteToOfflineIfNeeded,
} from './offlineWriteQueue';
import { ReminderCert } from '../types/reminders';
import { ReviewReminder } from '../types/reviews';
import { CalendarEvent } from '../types/crm';
import { EmailAutomationOrchestrator } from './emailAutomationOrchestrator';
import { normalizeCustomerNameFields, normalizeContactFields } from '../utils/textFormatting';
import { dedupeCustomerLocations, normalizeCompanyNameForMatching } from '../utils/customerRecordPersistence';

// Types for Supabase storage
interface Booking {
  id: string;
  customerId: string;
  locationId?: string;
  contactId?: string;
  instructorId?: string;
  courseName: string;
  courseDate: string;
  startTime?: string;
  endTime?: string;
  totalPrice?: number;
  instructorPrice?: number;
  instructorPO?: string;
  clientPO?: string;
  attendeeCount: number;
  notes?: string;
  status: 'scheduled' | 'confirmed' | 'reminder_email_sent' | 'reminder_email_2_sent' | 'certs_produced' | 'job_paid' | 'completed' | 'cancelled';
  createdAt: string;
  updatedAt: string;
  allBookingDates?: string[];
  allBookingDatesWithTimes?: Array<{date: string, startTime: string, endTime: string}>;
}

interface Instructor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

type CustomerFinancialColsPayload = {
  num_certs_generated?: number;
  is_paid?: boolean;
  last_payment_date?: string | null;
  payment_history?: unknown[];
  current_batch_certificates?: unknown[];
  generated_certificates?: unknown[];
};

/** DB `customers.name` is NOT NULL. Companies use the trading name; individuals use first + last only (never employer). */
function customerDbDisplayName(customer: Customer): string {
  if (customer.customerType === 'company') {
    return (customer.companyName || '').trim() || 'Unnamed company';
  }
  return `${customer.firstName || ''} ${customer.surname || ''}`.trim() || 'Unnamed customer';
}

function mapDbCustomerScalarFields(row: Record<string, unknown>) {
  return {
    sageCode: row.sage_code ? String(row.sage_code) : undefined,
    website: row.website ? String(row.website) : undefined,
    mobilePhone: row.mobile_phone ? String(row.mobile_phone) : undefined,
    customerSince: row.customer_since ? String(row.customer_since) : undefined,
  };
}

function customerSageImportWriteFields(customer: Customer) {
  const customerSince = customer.customerSince
    ? customer.customerSince.match(/^\d{4}-\d{2}-\d{2}$/)
      ? customer.customerSince
      : null
    : null;
  return {
    sage_code: customer.sageCode || null,
    website: customer.website || null,
    mobile_phone: customer.mobilePhone || null,
    customer_since: customerSince,
  };
}

function buildCustomerWritePayload(normalizedCustomer: Customer, includeId = false) {
  return {
    ...(includeId ? { id: normalizedCustomer.id } : {}),
    name: customerDbDisplayName(normalizedCustomer),
    customer_type: normalizedCustomer.customerType || 'company',
    company_role: normalizedCustomer.companyRole || null,
    company_name: normalizedCustomer.companyName,
    preferred_language: normalizedCustomer.preferredLanguage || null,
    first_name: normalizedCustomer.firstName || null,
    surname: normalizedCustomer.surname || null,
    contact_phone_number: normalizedCustomer.contactPhoneNumber || null,
    email_address: normalizedCustomer.emailAddress || null,
    industry: normalizedCustomer.industry ?? null,
    general_notes: normalizedCustomer.generalNotes ?? null,
    ...customerSageImportWriteFields(normalizedCustomer),
    course_fee: normalizedCustomer.courseFee ?? null,
    payment_on_the_day_type: normalizedCustomer.paymentOnTheDayType || null,
    pre_payment_amount: normalizedCustomer.prePaymentAmount ?? null,
    prepayment_payment_type: normalizedCustomer.prepaymentPaymentType || null,
    booked_on: normalizedCustomer.bookedOn || null,
    booked_by: normalizedCustomer.bookedBy || null,
    mh_set_up: normalizedCustomer.mhSetUp ?? null,
    invoice_number: normalizedCustomer.invoiceNumber || null,
    other_info_notes: normalizedCustomer.otherInfoNotes || null,
    ihasco_completed: normalizedCustomer.ihascoCompleted ?? null,
    safe_pass_deposit_amount: normalizedCustomer.safePassDepositAmount ?? null,
    safe_pass_deposit_type: normalizedCustomer.safePassDepositType || null,
    safe_pass_deposit_date: normalizedCustomer.safePassDepositDate || null,
    safe_pass_manual_handling: normalizedCustomer.safePassManualHandling ?? null,
    safe_pass_process_number: normalizedCustomer.safePassProcessNumber || null,
    safe_pass_card_posted_on: normalizedCustomer.safePassCardPostedOn || null,
    safe_pass_post_to_address: normalizedCustomer.safePassPostToAddress || null,
    safe_pass_card_status: normalizedCustomer.safePassCardStatus || 'not_issued',
  };
}

function buildCustomerFinancialPayload(normalizedCustomer: Customer): CustomerFinancialColsPayload {
  return {
    num_certs_generated: normalizedCustomer.numCertsGenerated ?? 0,
    is_paid: normalizedCustomer.isPaid ?? false,
    last_payment_date: normalizedCustomer.lastPaymentDate || null,
    payment_history: normalizedCustomer.paymentHistory ?? [],
    current_batch_certificates: normalizedCustomer.currentBatchCertificates ?? [],
    generated_certificates: normalizedCustomer.generatedCertificates ?? [],
  };
}

function stripUndefinedFields(row: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(row).filter(([, value]) => value !== undefined));
}

function formatSupabaseError(error: unknown): string {
  if (!error || typeof error !== 'object') return 'Unknown database error';
  const err = error as { message?: string; details?: string; hint?: string; code?: string };
  return [err.message, err.details, err.hint, err.code].filter(Boolean).join(' — ');
}

const BULK_IMPORT_BATCH_SIZE = 50;

function mapDbLocationRow(loc: Record<string, unknown>): Location {
  const primaryAddress = String(
    loc.address_line_1 || loc.address || loc.location_name || loc.name || ''
  ).trim();
  return {
    id: String(loc.id),
    addressLine1: primaryAddress,
    addressLine2: String(loc.address_line_2 || '').trim(),
    addressLine3: String(loc.address_line_3 || '').trim(),
    city: String(loc.city || ''),
    eircodePostcode: String(loc.eircode_postcode || loc.postcode || ''),
    countyState: String(loc.county_state || loc.county || ''),
    country: String(loc.country || ''),
    locationContactName: String(loc.location_contact_name || loc.contact_name || ''),
    locationContactEmail: (loc.location_contact_email ?? loc.contact_email ?? null) as string | null,
    locationContactPhone: String(loc.location_contact_phone || loc.contact_phone || ''),
    locationNotes: String(loc.location_notes || loc.notes || ''),
  };
}

function locationDbWritePayload(customerId: string, location: Location): Record<string, unknown> {
  const address = location.addressLine1?.trim() || '';
  return {
    customer_id: customerId,
    name: address.slice(0, 80) || 'Primary',
    address,
    city: location.city || '—',
    county: location.countyState || null,
    postcode: location.eircodePostcode || null,
    country: location.country || 'Ireland',
    contact_name: location.locationContactName || null,
    contact_email: location.locationContactEmail ?? null,
    contact_phone: location.locationContactPhone || null,
    notes: location.locationNotes || null,
    is_active: true,
    is_default: true,
    updated_at: new Date().toISOString(),
  };
}

function mapDbContactRow(contact: Record<string, unknown>): Contact {
  const isPrimary = !!(contact.is_billing_contact ?? contact.is_general_contact ?? contact.is_primary);
  return {
    id: String(contact.id),
    firstName: String(contact.first_name || ''),
    lastName: String(contact.last_name || ''),
    role: String(contact.role || contact.job_title || ''),
    email: String(contact.email || ''),
    phoneNumber: String(contact.phone_number || contact.phone || ''),
    isBillingContact: contact.is_billing_contact != null ? !!contact.is_billing_contact : isPrimary,
    isGeneralContact: contact.is_general_contact != null ? !!contact.is_general_contact : isPrimary,
    contactNotes: String(contact.contact_notes || ''),
  };
}

function contactDbWritePayload(customerId: string, contact: Contact): Record<string, unknown> {
  return {
    customer_id: customerId,
    first_name: contact.firstName,
    last_name: contact.lastName,
    email: contact.email,
    phone: contact.phoneNumber || null,
    job_title: contact.role || null,
    is_primary: !!(contact.isBillingContact || contact.isGeneralContact),
    updated_at: new Date().toISOString(),
  };
}

// Customer Storage
export const supabaseCustomerStorage = {
  async getCustomers(): Promise<Customer[]> {
    if (!isSupabaseConfigured()) {
      // Try to return cached data even if not configured
      const { localCustomerStorage } = await import('./localStorageCache');
      const cachedData = localCustomerStorage.getData<Customer>();
      if (cachedData && cachedData.length > 0) {
        console.log('⚠️ Using cached customer data (Supabase not configured)');
        return cachedData;
      }
      return [];
    }

    // Import cache at function level to avoid circular dependencies
    const { localCustomerStorage } = await import('./localStorageCache');

    try {
      // Fetch all customers with pagination to handle large datasets
      // Supabase has a default limit of 1000 rows, so we need to paginate
      let allCustomers: any[] = [];
      let hasMore = true;
      let offset = 0;
      const pageSize = 1000; // Supabase default limit
      
      while (hasMore) {
        const { data, error } = await supabase
          .from('customers')
          .select('*')
          .order('company_name')
          .range(offset, offset + pageSize - 1);
        
        if (error) {
          throw error;
        }
        
        if (data && data.length > 0) {
          allCustomers = [...allCustomers, ...data];
          // If we got fewer than pageSize, we've reached the end
          hasMore = data.length === pageSize;
          if (hasMore) {
            offset += pageSize;
          }
        } else {
          hasMore = false;
        }
      }
      
      // Extract all customer IDs for batch fetching
      const customerIds = allCustomers.map((c: any) => c.id);

      // Chunk IDs to keep URL length safe (avoid ERR_HTTP2_PROTOCOL_ERROR with huge in.(...) query)
      const IN_QUERY_BATCH_SIZE = 80;
      const chunk = <T>(arr: T[], size: number): T[][] => {
        const out: T[][] = [];
        for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
        return out;
      };

      // Batch fetch all locations and contacts - chunk customer_id IN lists to avoid URL too long
      const fetchAllFromTable = async (table: string, idColumn: string) => {
        const allData: any[] = [];
        const idChunks = chunk(customerIds, IN_QUERY_BATCH_SIZE);
        for (const idBatch of idChunks) {
          let offset = 0;
          const pageSize = 1000;
          let hasMore = true;
          while (hasMore) {
            const { data, error } = await supabase
              .from(table)
              .select('*')
              .in(idColumn, idBatch)
              .range(offset, offset + pageSize - 1);
            if (error) throw error;
            const rows = data || [];
            allData.push(...rows);
            hasMore = rows.length === pageSize;
            offset += pageSize;
          }
        }
        return allData;
      };

      const [allLocations, allContacts] = await Promise.all([
        customerIds.length > 0 ? fetchAllFromTable('locations', 'customer_id') : Promise.resolve([]),
        customerIds.length > 0 ? fetchAllFromTable('contacts', 'customer_id') : Promise.resolve([])
      ]);

      // Group locations and contacts by customer_id
      const locationsByCustomer = new Map<string, any[]>();
      allLocations.forEach((loc: any) => {
        const customerId = loc.customer_id;
        if (!locationsByCustomer.has(customerId)) {
          locationsByCustomer.set(customerId, []);
        }
        locationsByCustomer.get(customerId)!.push(loc);
      });

      const contactsByCustomer = new Map<string, any[]>();
      allContacts.forEach((contact: any) => {
        const customerId = contact.customer_id;
        if (!contactsByCustomer.has(customerId)) {
          contactsByCustomer.set(customerId, []);
        }
        contactsByCustomer.get(customerId)!.push(contact);
      });

      // Deduplicate customers by ID (in case pagination returns duplicates)
      const uniqueCustomersMap = new Map<string, any>();
      allCustomers.forEach((customer: any) => {
        if (!uniqueCustomersMap.has(customer.id)) {
          uniqueCustomersMap.set(customer.id, customer);
        }
      });
      const uniqueCustomers = Array.from(uniqueCustomersMap.values());

      // Map customers with their batched locations and contacts.
      // Include the same identity fields as getCustomerById so list rows are usable for quotes
      // (email / type / names) without waiting for a second fetch.
      const customers = uniqueCustomers.map((customer: any) => {
        const customerLocations = locationsByCustomer.get(customer.id) || [];
        const rawContacts = contactsByCustomer.get(customer.id) || [];
        const seenContactIds = new Set<string>();
        const customerContacts = rawContacts.filter((c: any) => {
          const id = String(c?.id || '');
          if (!id || seenContactIds.has(id)) return false;
          seenContactIds.add(id);
          return true;
        });

        return {
          id: customer.id,
          recordCreatedAt: customer.created_at || undefined,
          customerType: customer.customer_type || undefined,
          companyRole: customer.company_role || null,
          companyName: customer.company_name || customer.name || '',
          preferredLanguage: customer.preferred_language || undefined,
          firstName: customer.first_name || undefined,
          surname: customer.surname || undefined,
          contactPhoneNumber: customer.contact_phone_number || undefined,
          emailAddress: customer.email_address || undefined,
          industry: customer.industry,
          generalNotes: customer.general_notes,
          ...mapDbCustomerScalarFields(customer),
          userId: customer.user_id || undefined,
          locations: dedupeCustomerLocations(customerLocations.map((loc: any) => mapDbLocationRow(loc))),
          contacts: customerContacts.map((contact: any) => mapDbContactRow(contact)),
          numCertsGenerated: customer.num_certs_generated || 0,
          isPaid: customer.is_paid || false,
          lastPaymentDate: customer.last_payment_date || '',
          paymentHistory: customer.payment_history || [],
          currentBatchCertificates: customer.current_batch_certificates || [],
          generatedCertificates: customer.generated_certificates || []
        };
      });

      // Cache the customer data
      localCustomerStorage.setData(customers);
      localCustomerStorage.updateLastSync();

      return customers;
    } catch (error: any) {
      console.error('Error fetching customers:', error);
      
      // Check if this is a network/service error (not auth error)
      const isNetworkError = error?.message?.includes('Failed to fetch') || 
                            error?.message?.includes('NetworkError') ||
                            error?.code === 'ECONNREFUSED' ||
                            error?.code === 'ETIMEDOUT';
      
      // Try to return cached data (even if stale) during network outages
      if (isNetworkError && localCustomerStorage.hasData()) {
        const cachedData = localCustomerStorage.getData<Customer>();
        if (cachedData && cachedData.length > 0) {
          console.log('⚠️ Using stale cached customer data due to network error');
          return cachedData;
        }
      }
      
      return [];
    }
  },

  // Server-side customer search (lightweight fields)
  async searchCustomers(
    term: string,
    limit: number = 10,
    category: import('../utils/customerSearch').CustomerSearchCategory = 'all'
  ): Promise<
    Array<
      Pick<
        Customer,
        | 'id'
        | 'companyName'
        | 'industry'
        | 'customerType'
        | 'firstName'
        | 'surname'
        | 'emailAddress'
        | 'contactPhoneNumber'
        | 'companyRole'
        | 'preferredLanguage'
        | 'sageCode'
      >
    >
  > {
    if (!isSupabaseConfigured()) {
      return [];
    }

    const {
      buildCustomerSearchOrClause,
      customerTypeFilterForCategory,
      looksLikePhoneQuery,
      phoneDigitsOnly,
      rankCustomersBySearch,
      sanitizeSearchTerm,
    } = await import('../utils/customerSearch');

    const safe = sanitizeSearchTerm(term);
    if (!safe) {
      return [];
    }

    const uuidLike =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(safe);

    const searchSelect =
      'id, company_name, industry, customer_type, company_role, preferred_language, first_name, surname, email_address, contact_phone_number, mobile_phone, sage_code';

    const mapRow = (customer: any) => ({
      id: customer.id,
      companyName: customer.company_name,
      industry: customer.industry || undefined,
      customerType: customer.customer_type || undefined,
      companyRole: customer.company_role || undefined,
      preferredLanguage: customer.preferred_language || undefined,
      firstName: customer.first_name || undefined,
      surname: customer.surname || undefined,
      emailAddress: customer.email_address || undefined,
      contactPhoneNumber: customer.contact_phone_number || undefined,
      mobilePhone: customer.mobile_phone || undefined,
      sageCode: customer.sage_code || undefined,
      generatedCertificates: Array.isArray(customer.generated_certificates)
        ? customer.generated_certificates
        : undefined,
    });

    const merged = new Map<string, ReturnType<typeof mapRow>>();
    const addRows = (rows: any[] | null | undefined) => {
      for (const row of rows || []) {
        if (!merged.has(row.id)) merged.set(row.id, mapRow(row));
      }
    };

    const orClause = buildCustomerSearchOrClause(safe, category);
    const typeFilter = customerTypeFilterForCategory(category);
    const digits = phoneDigitsOnly(safe);
    const wantPhoneDigits =
      digits.length >= 6 && (category === 'phone' || category === 'all' || looksLikePhoneQuery(safe));

    try {
      const tasks: Promise<any>[] = [];

      if (category === 'all' || category === 'sage') {
        let sageQ = supabase
          .from('customers')
          .select(searchSelect)
          .ilike('sage_code', `%${safe}%`)
          .order('company_name')
          .limit(limit);
        if (typeFilter) sageQ = sageQ.eq('customer_type', typeFilter);
        tasks.push(Promise.resolve(sageQ));
      }

      if (orClause) {
        const clause = uuidLike && category === 'all' ? `${orClause},id.eq.${safe}` : orClause;
        let textQ = supabase
          .from('customers')
          .select(searchSelect)
          .or(clause)
          .order('company_name')
          .limit(Math.max(limit * 3, 30));
        if (typeFilter) textQ = textQ.eq('customer_type', typeFilter);
        tasks.push(Promise.resolve(textQ));
      }

      if (wantPhoneDigits) {
        tasks.push(
          (async () => {
            const { data: ids, error } = await supabase.rpc('customer_ids_matching_phone_digits', {
              p_digits: digits,
              p_limit: limit,
            });
            if (error || !ids?.length) return { data: [], error };
            let phoneQ = supabase.from('customers').select(searchSelect).in('id', ids);
            if (typeFilter) phoneQ = phoneQ.eq('customer_type', typeFilter);
            return phoneQ;
          })()
        );
      }

      if (category === 'all' || category === 'individual' || category === 'company') {
        tasks.push(
          (async () => {
            const { data: ids, error } = await supabase.rpc('customer_ids_matching_certificate_text', {
              p_q: safe,
              p_limit: Math.max(limit * 3, 40),
            });
            if (error || !ids?.length) return { data: [], error };
            let certQ = supabase
              .from('customers')
              .select(`${searchSelect}, generated_certificates`)
              .in('id', ids);
            if (typeFilter) certQ = certQ.eq('customer_type', typeFilter);
            return certQ;
          })()
        );
      }

      const results = await Promise.all(tasks);
      for (const res of results) {
        if (res?.error) {
          if (!/sage_code|mobile_phone|schema cache|customer_ids_matching/i.test(formatSupabaseError(res.error))) {
            console.warn('Customer search partial failure:', res.error);
          }
        } else {
          addRows(res?.data);
        }
      }

      // Certificate attendee names from local cache (Global Search source)
      if (category === 'all' || category === 'individual' || category === 'company') {
        try {
          const { localCustomerStorage } = await import('./localStorageCache');
          const {
            customerMatchesCertificateSearch,
            resolveCustomerByPersonName,
          } = await import('../utils/customerSearch');
          const cached = localCustomerStorage.getData<Customer>() || [];
          for (const c of cached) {
            if (!customerMatchesCertificateSearch(c, safe)) continue;
            if (!merged.has(c.id)) {
              merged.set(c.id, {
                id: c.id,
                companyName: c.companyName,
                industry: c.industry,
                customerType: c.customerType,
                companyRole: c.companyRole,
                preferredLanguage: c.preferredLanguage,
                firstName: c.firstName,
                surname: c.surname,
                emailAddress: c.emailAddress,
                contactPhoneNumber: c.contactPhoneNumber,
                mobilePhone: c.mobilePhone,
                sageCode: c.sageCode,
                generatedCertificates: c.generatedCertificates,
              } as any);
            } else {
              const existing = merged.get(c.id) as any;
              if (!existing.generatedCertificates?.length && c.generatedCertificates?.length) {
                existing.generatedCertificates = c.generatedCertificates;
              }
            }
            const personCert = (c.generatedCertificates || []).find((cert) =>
              String(cert?.attendeeName || '')
                .toLowerCase()
                .includes(safe.toLowerCase())
            );
            if (personCert?.attendeeName) {
              const individual = resolveCustomerByPersonName(personCert.attendeeName, cached);
              if (individual && !merged.has(individual.id)) {
                merged.set(individual.id, {
                  id: individual.id,
                  companyName: individual.companyName,
                  industry: individual.industry,
                  customerType: individual.customerType,
                  companyRole: individual.companyRole,
                  preferredLanguage: individual.preferredLanguage,
                  firstName: individual.firstName,
                  surname: individual.surname,
                  emailAddress: individual.emailAddress,
                  contactPhoneNumber: individual.contactPhoneNumber,
                  mobilePhone: individual.mobilePhone,
                  sageCode: individual.sageCode,
                } as any);
              }
            }
          }
        } catch {
          /* ignore */
        }
      }

      return rankCustomersBySearch(Array.from(merged.values()) as any[], safe, category)
        .slice(0, limit)
        .map((row: any) => {
          const { mobilePhone: _m, generatedCertificates: _g, ...rest } = row;
          return rest;
        });
    } catch (error) {
      console.error('Error searching customers:', error);
      try {
        let fallback = supabase
          .from('customers')
          .select(searchSelect)
          .or(orClause || `company_name.ilike.%${safe}%`)
          .order('company_name')
          .limit(limit);
        if (typeFilter) fallback = fallback.eq('customer_type', typeFilter);
        const { data, error: fallbackError } = await fallback;
        if (fallbackError) throw fallbackError;
        return rankCustomersBySearch((data || []).map(mapRow) as any[], safe, category)
          .slice(0, limit)
          .map((row: any) => {
            const { mobilePhone: _m, ...rest } = row;
            return rest;
          });
      } catch (fallbackError) {
        console.error('Customer search fallback failed:', fallbackError);
        return [];
      }
    }
  },

  // Lightweight version for list views - only essential fields
  async getCustomersList(
    limit?: number,
    offset?: number,
    searchTerm?: string,
    category: import('../utils/customerSearch').CustomerSearchCategory = 'all'
  ): Promise<Customer[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    // Import cache at function level to avoid circular dependencies
    const { localCustomerStorage } = await import('./localStorageCache');

    // Only use cache if we're loading all data (no pagination/search)
    if (limit === undefined && offset === undefined && !searchTerm) {
      if (localCustomerStorage.hasFreshData(15 * 60 * 1000)) { // 15 minutes
        const cachedData = localCustomerStorage.getData<Customer>();
        if (cachedData) {
          // Using cached customer data - log removed to reduce console noise
          return cachedData;
        }
      }
    }

    try {
      // Only log if not a duplicate request (reduce console noise)
      if (limit !== undefined && offset !== undefined) {
        console.log('🌐 Fetching customers from Supabase', { limit, offset, searchTerm });
      }
      
      const CUSTOMER_LIST_SELECT =
        'id, customer_type, company_role, company_name, preferred_language, first_name, surname, contact_phone_number, email_address, industry, general_notes, sage_code, website, mobile_phone, customer_since, user_id, course_fee, payment_on_the_day_type, pre_payment_amount, prepayment_payment_type, booked_on, booked_by, mh_set_up, invoice_number, other_info_notes, ihasco_completed, safe_pass_deposit_amount, safe_pass_deposit_type, safe_pass_deposit_date, safe_pass_manual_handling, safe_pass_process_number, safe_pass_card_posted_on, safe_pass_post_to_address, safe_pass_card_status, num_certs_generated, is_paid, last_payment_date, payment_history, current_batch_certificates, generated_certificates';

      let customersQuery = supabase.from('customers').select(CUSTOMER_LIST_SELECT);

      // When searching: pull a large candidate pool, rank client-side, then page.
      // Ordering by company_name + limit 20 was burying prefix matches (most names "not found").
      let searchCandidateLimit: number | null = null;
      let searchPageOffset = 0;
      let searchPageLimit: number | undefined = limit;
      let extraPhoneIds: string[] = [];
      let extraNameIds: string[] = [];

      if (searchTerm) {
        const {
          CUSTOMER_SEARCH_CANDIDATE_LIMIT,
          buildCustomerSearchOrClause,
          customerTypeFilterForCategory,
          looksLikePhoneQuery,
          phoneDigitsOnly,
          sanitizeSearchTerm,
          searchTokens,
        } = await import('../utils/customerSearch');
        const safeSearch = sanitizeSearchTerm(searchTerm);
        const orClause = buildCustomerSearchOrClause(safeSearch, category);
        const typeFilter = customerTypeFilterForCategory(category);
        if (typeFilter) {
          customersQuery = customersQuery.eq('customer_type', typeFilter);
        }
        if (orClause) {
          customersQuery = customersQuery.or(orClause);
        }
        const digits = phoneDigitsOnly(safeSearch);
        if (
          digits.length >= 6 &&
          (category === 'phone' || category === 'all' || looksLikePhoneQuery(safeSearch))
        ) {
          const { data: phoneIds } = await supabase.rpc('customer_ids_matching_phone_digits', {
            p_digits: digits,
            p_limit: Math.max(CUSTOMER_SEARCH_CANDIDATE_LIMIT, 80),
          });
          if (phoneIds?.length) {
            if (category === 'phone') {
              customersQuery = supabase
                .from('customers')
                .select(CUSTOMER_LIST_SELECT)
                .in('id', phoneIds);
              if (typeFilter) customersQuery = customersQuery.eq('customer_type', typeFilter);
            } else {
              extraPhoneIds = phoneIds.map(String);
            }
          }
        }

        // Also find people named on enrollment rows / certificate JSON (e.g. "Davor Kraljevic")
        if (category === 'all' || category === 'individual' || category === 'company') {
          const tokens = searchTokens(safeSearch);
          const nameOr = tokens
            .flatMap((t) => [
              `enrollment_first_name.ilike.%${t}%`,
              `enrollment_surname.ilike.%${t}%`,
            ])
            .join(',');
          if (nameOr) {
            const { data: attRows } = await supabase
              .from('course_session_attendees')
              .select('individual_customer_id, company_customer_id, enrollment_first_name, enrollment_surname')
              .or(nameOr)
              .limit(120);
            for (const row of attRows || []) {
              if (row.individual_customer_id) extraNameIds.push(String(row.individual_customer_id));
              if (row.company_customer_id) extraNameIds.push(String(row.company_customer_id));
            }
          }

          const { data: certIds, error: certRpcError } = await supabase.rpc(
            'customer_ids_matching_certificate_text',
            { p_q: safeSearch, p_limit: 80 }
          );
          if (!certRpcError && Array.isArray(certIds)) {
            for (const id of certIds) {
              if (id) extraNameIds.push(String(id));
            }
          }
        }

        searchCandidateLimit = CUSTOMER_SEARCH_CANDIDATE_LIMIT;
        searchPageOffset = offset ?? 0;
        searchPageLimit = limit;
      } else if (category === 'company' || category === 'individual') {
        customersQuery = customersQuery.eq('customer_type', category);
      }

      customersQuery = customersQuery.order('company_name').order('id');

      if (searchCandidateLimit != null) {
        customersQuery = customersQuery.limit(searchCandidateLimit);
      } else if (offset !== undefined && limit !== undefined) {
        customersQuery = customersQuery.range(offset, offset + limit - 1);
      } else if (limit !== undefined) {
        customersQuery = customersQuery.limit(limit);
      }

      const { data: customersDataRaw, error: customersError } = await customersQuery;

      if (customersError) {
        throw customersError;
      }

      let customersData = customersDataRaw || [];

      const mergeExtraIds = async (ids: string[]) => {
        const have = new Set(customersData.map((c: any) => c.id));
        const missing = [...new Set(ids)].filter((id) => id && !have.has(id)).slice(0, 120);
        if (!missing.length) return;
        const { data: extraRows } = await supabase
          .from('customers')
          .select(CUSTOMER_LIST_SELECT)
          .in('id', missing);
        if (extraRows?.length) {
          customersData = [...customersData, ...extraRows];
        }
      };

      if (extraPhoneIds.length > 0) await mergeExtraIds(extraPhoneIds);
      if (extraNameIds.length > 0) await mergeExtraIds(extraNameIds);

      // Certificate attendee names (same source Global Search uses) — merge local cache
      // rows so ranking can see attendeeName even when DB name columns don't match.
      let cacheCertCustomers: Customer[] = [];
      if (searchTerm && (category === 'all' || category === 'individual' || category === 'company')) {
        try {
          const {
            customerMatchesCertificateSearch,
            resolveCustomerByPersonName,
            sanitizeSearchTerm: sanitize,
          } = await import('../utils/customerSearch');
          const cached = localCustomerStorage.getData<Customer>() || [];
          const safe = sanitize(searchTerm);
          const have = new Set(customersData.map((c: any) => c.id));
          const extraFromCache: string[] = [];
          for (const c of cached) {
            if (!customerMatchesCertificateSearch(c, safe)) continue;
            cacheCertCustomers.push(c);
            if (!have.has(c.id)) extraFromCache.push(c.id);
            const personCert = (c.generatedCertificates || []).find((cert) =>
              String(cert?.attendeeName || '')
                .toLowerCase()
                .includes(safe.toLowerCase())
            );
            if (personCert?.attendeeName) {
              const individual = resolveCustomerByPersonName(personCert.attendeeName, cached);
              if (individual?.id && !have.has(individual.id)) {
                extraFromCache.push(individual.id);
                cacheCertCustomers.push(individual);
              }
            }
          }
          if (extraFromCache.length) await mergeExtraIds(extraFromCache);
        } catch {
          /* ignore cache merge failures */
        }
      }

      if (!customersData || customersData.length === 0) {
        // Still allow cache-only certificate hits (e.g. attendee name only on company cert JSON)
        if (cacheCertCustomers.length === 0) return [];
      }

      // Batch fetch all locations and contacts for all customer IDs
      // Chunk customer_id IN lists to avoid URL too long (ERR_HTTP2_PROTOCOL_ERROR)
      const customerIds = customersData.map((c: any) => c.id);
      const pageSize = 1000;
      const IN_BATCH = 80;
      const chunkIds = <T>(arr: T[], size: number): T[][] => {
        const out: T[][] = [];
        for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
        return out;
      };

      const fetchAllFromTable = async (table: string, idColumn: string) => {
        const allData: any[] = [];
        for (const idBatch of chunkIds(customerIds, IN_BATCH)) {
          let offset = 0;
          let hasMore = true;
          while (hasMore) {
            const { data, error } = await supabase
              .from(table)
              .select('*')
              .in(idColumn, idBatch)
              .range(offset, offset + pageSize - 1);
            if (error) throw error;
            const rows = data || [];
            allData.push(...rows);
            hasMore = rows.length === pageSize;
            offset += pageSize;
          }
        }
        return allData;
      };

      const [allLocations, allContacts] = await Promise.all([
        customerIds.length > 0 ? fetchAllFromTable('locations', 'customer_id') : Promise.resolve([]),
        customerIds.length > 0 ? fetchAllFromTable('contacts', 'customer_id') : Promise.resolve([])
      ]);

      // Group locations and contacts by customer_id
      const locationsByCustomer = new Map<string, any[]>();
      allLocations.forEach((loc: any) => {
        const customerId = loc.customer_id;
        if (!locationsByCustomer.has(customerId)) {
          locationsByCustomer.set(customerId, []);
        }
        locationsByCustomer.get(customerId)!.push(loc);
      });

      const contactsByCustomer = new Map<string, any[]>();
      allContacts.forEach((contact: any) => {
        const customerId = contact.customer_id;
        if (!contactsByCustomer.has(customerId)) {
          contactsByCustomer.set(customerId, []);
        }
        contactsByCustomer.get(customerId)!.push(contact);
      });

      // Map customers with their batched locations and contacts
      const customers = customersData.map((customer: any) => {
        const customerLocations = locationsByCustomer.get(customer.id) || [];
        const customerContacts = contactsByCustomer.get(customer.id) || [];

        return {
          id: customer.id,
          customerType: customer.customer_type || 'company',
          companyRole: customer.company_role || null,
          companyName: customer.company_name || customer.name || '',
          preferredLanguage: customer.preferred_language || undefined,
          firstName: customer.first_name || undefined,
          surname: customer.surname || undefined,
          contactPhoneNumber: customer.contact_phone_number || undefined,
          emailAddress: customer.email_address || undefined,
          industry: customer.industry,
          generalNotes: customer.general_notes,
          ...mapDbCustomerScalarFields(customer),
          userId: customer.user_id || undefined,
          courseFee: customer.course_fee ?? null,
          paymentOnTheDayType: customer.payment_on_the_day_type || undefined,
          prePaymentAmount: customer.pre_payment_amount ?? null,
          prepaymentPaymentType: customer.prepayment_payment_type || undefined,
          bookedOn: customer.booked_on || undefined,
          bookedBy: customer.booked_by || undefined,
          mhSetUp: customer.mh_set_up ?? undefined,
          invoiceNumber: customer.invoice_number || undefined,
          otherInfoNotes: customer.other_info_notes || undefined,
          ihascoCompleted: customer.ihasco_completed ?? undefined,
          safePassDepositAmount: customer.safe_pass_deposit_amount ?? null,
          safePassDepositType: customer.safe_pass_deposit_type || undefined,
          safePassDepositDate: customer.safe_pass_deposit_date || undefined,
          safePassManualHandling: customer.safe_pass_manual_handling ?? undefined,
          safePassProcessNumber: customer.safe_pass_process_number || undefined,
          safePassCardPostedOn: customer.safe_pass_card_posted_on || undefined,
          safePassPostToAddress: customer.safe_pass_post_to_address || undefined,
          safePassCardStatus: customer.safe_pass_card_status || 'not_issued',
          locations: dedupeCustomerLocations(customerLocations.map((loc: any) => mapDbLocationRow(loc))),
          contacts: customerContacts.map((contact: any) => mapDbContactRow(contact)),
          numCertsGenerated: customer.num_certs_generated || 0,
          isPaid: customer.is_paid || false,
          lastPaymentDate: customer.last_payment_date || '',
          paymentHistory: Array.isArray(customer.payment_history) ? customer.payment_history : [],
          currentBatchCertificates: Array.isArray(customer.current_batch_certificates)
            ? customer.current_batch_certificates
            : [],
          generatedCertificates: Array.isArray(customer.generated_certificates)
            ? customer.generated_certificates
            : []
        };
      });

      // Only cache if we loaded all data (no pagination/search)
      if (limit === undefined && offset === undefined && !searchTerm) {
        localCustomerStorage.setData(customers);
        localCustomerStorage.updateLastSync();
      }

      if (searchTerm) {
        const { rankCustomersBySearch, sanitizeSearchTerm } = await import('../utils/customerSearch');
        // Overlay certificate JSON from local cache so attendee-name hits rank correctly
        const byId = new Map(customers.map((c) => [c.id, c]));
        for (const cached of cacheCertCustomers) {
          const existing = byId.get(cached.id);
          if (!existing) {
            byId.set(cached.id, {
              ...cached,
              locations: cached.locations || [],
              contacts: cached.contacts || [],
            });
            continue;
          }
          if (
            (!existing.generatedCertificates?.length ||
              existing.generatedCertificates.length < (cached.generatedCertificates?.length || 0)) &&
            cached.generatedCertificates?.length
          ) {
            byId.set(cached.id, {
              ...existing,
              generatedCertificates: cached.generatedCertificates,
              numCertsGenerated: cached.numCertsGenerated ?? existing.numCertsGenerated,
            });
          }
        }
        const ranked = rankCustomersBySearch(
          Array.from(byId.values()),
          sanitizeSearchTerm(searchTerm),
          category
        );
        if (searchPageLimit != null) {
          return ranked.slice(searchPageOffset, searchPageOffset + searchPageLimit);
        }
        return ranked;
      }

      return customers;
    } catch (error: any) {
      console.error('Error fetching customers list:', error);

      // Check if this is a network/service error (not auth error)
      const isNetworkError = error?.message?.includes('Failed to fetch') || 
                            error?.message?.includes('NetworkError') ||
                            error?.code === 'ECONNREFUSED' ||
                            error?.code === 'ETIMEDOUT';

      // If fetch fails due to network error, try to return cached data even if stale
      if (isNetworkError && localCustomerStorage.hasData()) {
      const cachedData = localCustomerStorage.getData<Customer>();
        if (cachedData && cachedData.length > 0) {
          console.log('⚠️ Using stale cached customer data due to network error');
        return cachedData;
        }
      }

      // If we have any cached data (even if not network error), try to use it
      if (localCustomerStorage.hasData()) {
        const cachedData = localCustomerStorage.getData<Customer>();
        if (cachedData && cachedData.length > 0) {
          console.log('⚠️ Using stale cached customer data as fallback');
          return cachedData;
        }
      }

      throw error;
    }
  },

  async getCustomersCount(
    searchTerm?: string,
    category: import('../utils/customerSearch').CustomerSearchCategory = 'all'
  ): Promise<number> {
    if (!isSupabaseConfigured()) {
      return 0;
    }

    try {
      const {
        buildCustomerSearchOrClause,
        customerTypeFilterForCategory,
        sanitizeSearchTerm,
      } = await import('../utils/customerSearch');
      const typeFilter = customerTypeFilterForCategory(category);
      const safe = searchTerm ? sanitizeSearchTerm(searchTerm) : '';

      if (!safe) {
        let query = supabase.from('customers').select('*', { count: 'exact', head: true });
        if (typeFilter) query = query.eq('customer_type', typeFilter);
        const { count, error } = await query;
        if (error) {
          console.error('Error fetching customers count:', error);
          return 0;
        }
        return count || 0;
      }

      // Search counts must include certificate / enrollment hits, not only name columns.
      const ids = new Set<string>();
      const orClause = buildCustomerSearchOrClause(safe, category);
      if (orClause) {
        let textQ = supabase.from('customers').select('id').or(orClause).limit(500);
        if (typeFilter) textQ = textQ.eq('customer_type', typeFilter);
        const { data } = await textQ;
        for (const row of data || []) ids.add(String(row.id));
      }

      if (category === 'all' || category === 'individual' || category === 'company') {
        const { data: certIds } = await supabase.rpc('customer_ids_matching_certificate_text', {
          p_q: safe,
          p_limit: 200,
        });
        for (const id of certIds || []) {
          if (id) ids.add(String(id));
        }
      }

      return ids.size;
    } catch (error) {
      console.error('Error in getCustomersCount:', error);
      return 0;
    }
  },

  /**
   * Customers referenced by ID only (e.g. Ops planner for bookings in view).
   * Same row shape as getCustomersList (including locations/contacts) without loading the full directory.
   */
  async getCustomersByIds(ids: string[]): Promise<Customer[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    const unique = [...new Set(ids.filter(Boolean))];
    if (unique.length === 0) {
      return [];
    }

    const listSelect =
      'id, customer_type, company_role, company_name, preferred_language, first_name, surname, contact_phone_number, email_address, industry, general_notes, user_id, course_fee, payment_on_the_day_type, pre_payment_amount, prepayment_payment_type, booked_on, booked_by, mh_set_up, invoice_number, other_info_notes, ihasco_completed, safe_pass_deposit_amount, safe_pass_deposit_type, safe_pass_deposit_date, safe_pass_manual_handling, safe_pass_process_number, safe_pass_card_posted_on, safe_pass_post_to_address, safe_pass_card_status, num_certs_generated, is_paid, last_payment_date, current_batch_certificates, generated_certificates';

    const IN_BATCH = 80;
    const chunkIds = <T>(arr: T[], size: number): T[][] => {
      const out: T[][] = [];
      for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
      return out;
    };

    try {
      const customersData: any[] = [];
      for (const idBatch of chunkIds(unique, IN_BATCH)) {
        const { data, error } = await supabase
          .from('customers')
          .select(listSelect)
          .in('id', idBatch)
          .order('company_name');
        if (error) throw error;
        if (data?.length) customersData.push(...data);
      }

      if (customersData.length === 0) {
        return [];
      }

      const customerIds = customersData.map((c: any) => c.id);
      const pageSize = 1000;
      const fetchAllFromTable = async (table: string, idColumn: string) => {
        const allData: any[] = [];
        for (const idBatch of chunkIds(customerIds, IN_BATCH)) {
          let offset = 0;
          let hasMore = true;
          while (hasMore) {
            const { data, error } = await supabase
              .from(table)
              .select('*')
              .in(idColumn, idBatch)
              .range(offset, offset + pageSize - 1);
            if (error) throw error;
            const rows = data || [];
            allData.push(...rows);
            hasMore = rows.length === pageSize;
            offset += pageSize;
          }
        }
        return allData;
      };

      const [allLocations, allContacts] = await Promise.all([
        customerIds.length > 0 ? fetchAllFromTable('locations', 'customer_id') : Promise.resolve([]),
        customerIds.length > 0 ? fetchAllFromTable('contacts', 'customer_id') : Promise.resolve([])
      ]);

      const locationsByCustomer = new Map<string, any[]>();
      allLocations.forEach((loc: any) => {
        const customerId = loc.customer_id;
        if (!locationsByCustomer.has(customerId)) {
          locationsByCustomer.set(customerId, []);
        }
        locationsByCustomer.get(customerId)!.push(loc);
      });

      const contactsByCustomer = new Map<string, any[]>();
      allContacts.forEach((contact: any) => {
        const customerId = contact.customer_id;
        if (!contactsByCustomer.has(customerId)) {
          contactsByCustomer.set(customerId, []);
        }
        contactsByCustomer.get(customerId)!.push(contact);
      });

      return customersData.map((customer: any) => {
        const customerLocations = locationsByCustomer.get(customer.id) || [];
        const rawContacts = contactsByCustomer.get(customer.id) || [];
        const seenContactIds = new Set<string>();
        const customerContacts = rawContacts.filter((c: any) => {
          const id = String(c?.id || '');
          if (!id || seenContactIds.has(id)) return false;
          seenContactIds.add(id);
          return true;
        });

        return {
          id: customer.id,
          customerType: customer.customer_type || 'company',
          companyRole: customer.company_role || null,
          companyName: customer.company_name || customer.name || '',
          preferredLanguage: customer.preferred_language || undefined,
          firstName: customer.first_name || undefined,
          surname: customer.surname || undefined,
          contactPhoneNumber: customer.contact_phone_number || undefined,
          emailAddress: customer.email_address || undefined,
          industry: customer.industry,
          generalNotes: customer.general_notes,
          ...mapDbCustomerScalarFields(customer),
          userId: customer.user_id || undefined,
          courseFee: customer.course_fee ?? null,
          paymentOnTheDayType: customer.payment_on_the_day_type || undefined,
          prePaymentAmount: customer.pre_payment_amount ?? null,
          prepaymentPaymentType: customer.prepayment_payment_type || undefined,
          bookedOn: customer.booked_on || undefined,
          bookedBy: customer.booked_by || undefined,
          mhSetUp: customer.mh_set_up ?? undefined,
          invoiceNumber: customer.invoice_number || undefined,
          otherInfoNotes: customer.other_info_notes || undefined,
          ihascoCompleted: customer.ihasco_completed ?? undefined,
          safePassDepositAmount: customer.safe_pass_deposit_amount ?? null,
          safePassDepositType: customer.safe_pass_deposit_type || undefined,
          safePassDepositDate: customer.safe_pass_deposit_date || undefined,
          safePassManualHandling: customer.safe_pass_manual_handling ?? undefined,
          safePassProcessNumber: customer.safe_pass_process_number || undefined,
          safePassCardPostedOn: customer.safe_pass_card_posted_on || undefined,
          safePassPostToAddress: customer.safe_pass_post_to_address || undefined,
          safePassCardStatus: customer.safe_pass_card_status || 'not_issued',
          locations: dedupeCustomerLocations(customerLocations.map((loc: any) => mapDbLocationRow(loc))),
          contacts: customerContacts.map((contact: any) => mapDbContactRow(contact)),
          numCertsGenerated: customer.num_certs_generated || 0,
          isPaid: customer.is_paid || false,
          lastPaymentDate: customer.last_payment_date || '',
          paymentHistory: [],
          currentBatchCertificates: Array.isArray(customer.current_batch_certificates)
            ? customer.current_batch_certificates
            : [],
          generatedCertificates: Array.isArray(customer.generated_certificates)
            ? customer.generated_certificates
            : []
        };
      });
    } catch (error) {
      console.error('Error in getCustomersByIds:', error);
      return [];
    }
  },

  /**
   * Minimal customer rows for admin notification heuristics (no locations/contacts).
   * Paginates the full table but transfers far less than getCustomers / getCustomersList.
   */
  async getCustomersForAdminNotifications(): Promise<Customer[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      const out: Customer[] = [];
      const pageSize = 1000;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('customers')
          .select('id, company_name, num_certs_generated, payment_history')
          .order('company_name')
          .range(offset, offset + pageSize - 1);

        if (error) throw error;
        const rows = data || [];
        for (const c of rows) {
          out.push({
            id: c.id,
            companyName: c.company_name || '',
            numCertsGenerated: c.num_certs_generated || 0,
            paymentHistory: Array.isArray(c.payment_history) ? c.payment_history : [],
            locations: [],
            contacts: [],
            generatedCertificates: []
          });
        }
        hasMore = rows.length === pageSize;
        offset += pageSize;
      }

      return out;
    } catch (error) {
      console.error('Error in getCustomersForAdminNotifications:', error);
      throw error;
    }
  },

  // Get customer certificates separately (for when needed)
  async getCustomerCertificates(customerId: string): Promise<{
    currentBatchCertificates: any[];
    generatedCertificates: any[];
    paymentHistory: any[];
  }> {
    if (!isSupabaseConfigured()) {
      return {
        currentBatchCertificates: [],
        generatedCertificates: [],
        paymentHistory: []
      };
    }

    try {
      const { data, error } = await supabase
        .from('customers')
        .select(`
          current_batch_certificates,
          generated_certificates,
          payment_history
        `)
        .eq('id', customerId)
        .single();
      
      if (error) {
        throw error;
      }

      return {
        currentBatchCertificates: data.current_batch_certificates || [],
        generatedCertificates: data.generated_certificates || [],
        paymentHistory: data.payment_history || []
      };
    } catch (error) {
      console.error('Error fetching customer certificates:', error);
      throw error;
    }
  },

  async getCustomerById(customerId: string): Promise<Customer | undefined> {
    if (!isSupabaseConfigured()) {
      return undefined;
    }

    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', customerId)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          return undefined;
        }
        throw error;
      }

      if (!data) return undefined;

      // Fetch locations and contacts in separate queries to avoid nested-select limits and ensure all rows are loaded
      const [locationsResult, contactsResult] = await Promise.all([
        supabase.from('locations').select('*').eq('customer_id', customerId).order('created_at', { ascending: true }),
        (async () => {
          let allContacts: any[] = [];
          let offset = 0;
          const pageSize = 1000;
          let hasMore = true;
          while (hasMore) {
            const { data: page, error: contactError } = await supabase
              .from('contacts')
              .select('*')
              .eq('customer_id', customerId)
              .range(offset, offset + pageSize - 1);
            if (contactError) throw contactError;
            const rows = page || [];
            allContacts = [...allContacts, ...rows];
            hasMore = rows.length === pageSize;
            offset += pageSize;
          }
          return { data: allContacts };
        })()
      ]);

      const directLocations = locationsResult.data || [];
      const directContacts = contactsResult.data || [];

      return {
        id: data.id,
        recordCreatedAt: data.created_at || undefined,
        customerType: data.customer_type || 'company',
        companyRole: data.company_role || null,
        companyName: data.company_name || data.name || '',
        preferredLanguage: data.preferred_language || undefined,
        firstName: data.first_name || undefined,
        surname: data.surname || undefined,
        contactPhoneNumber: data.contact_phone_number || undefined,
        emailAddress: data.email_address || undefined,
        industry: data.industry,
        generalNotes: data.general_notes,
        ...mapDbCustomerScalarFields(data),
        userId: data.user_id || undefined,
        courseFee: data.course_fee ?? null,
        paymentOnTheDayType: data.payment_on_the_day_type || undefined,
        prePaymentAmount: data.pre_payment_amount ?? null,
        prepaymentPaymentType: data.prepayment_payment_type || undefined,
        bookedOn: data.booked_on || undefined,
        bookedBy: data.booked_by || undefined,
        mhSetUp: data.mh_set_up ?? undefined,
        invoiceNumber: data.invoice_number || undefined,
        otherInfoNotes: data.other_info_notes || undefined,
        ihascoCompleted: data.ihasco_completed ?? undefined,
        safePassDepositAmount: data.safe_pass_deposit_amount ?? null,
        safePassDepositType: data.safe_pass_deposit_type || undefined,
        safePassDepositDate: data.safe_pass_deposit_date || undefined,
        safePassManualHandling: data.safe_pass_manual_handling ?? undefined,
        safePassProcessNumber: data.safe_pass_process_number || undefined,
        safePassCardPostedOn: data.safe_pass_card_posted_on || undefined,
        safePassPostToAddress: data.safe_pass_post_to_address || undefined,
        safePassCardStatus: data.safe_pass_card_status || 'not_issued',
        locations: dedupeCustomerLocations(directLocations.map((loc: any) => mapDbLocationRow(loc))),
        contacts: directContacts.map((contact: any) => mapDbContactRow(contact)),
        numCertsGenerated: data.num_certs_generated || 0,
        isPaid: data.is_paid || false,
        lastPaymentDate: data.last_payment_date || '',
        paymentHistory: data.payment_history || [],
        currentBatchCertificates: data.current_batch_certificates || [],
        generatedCertificates: data.generated_certificates || []
      };
    } catch (error) {
      console.error('Error fetching customer by ID:', error);
      return undefined;
    }
  },

  async getCustomerNames(): Promise<Customer[]> {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .order('companyName');
    
    if (error) throw error;
    return data || [];
  },

  async getCustomerByName(companyName: string): Promise<Customer | undefined> {
    if (!isSupabaseConfigured()) {
      return undefined;
    }

    try {
      const { data, error } = await supabase
        .from('customers')
        .select(`
          *,
          locations(*),
          contacts(*)
        `)
        .eq('company_name', companyName)
        .maybeSingle(); // Use maybeSingle() instead of single() to handle 0 or multiple rows gracefully
      
      if (error) {
        // PGRST116 means multiple rows or no rows - both are fine for this use case
        if (error.code === 'PGRST116') {
          return undefined;
        }
        throw error;
      }

      if (!data) return undefined;

      return {
        id: data.id,
        customerType: data.customer_type || 'company',
        companyRole: data.company_role || null,
        companyName: data.company_name || data.name || '',
        preferredLanguage: data.preferred_language || undefined,
        firstName: data.first_name || undefined,
        surname: data.surname || undefined,
        contactPhoneNumber: data.contact_phone_number || undefined,
        emailAddress: data.email_address || undefined,
        industry: data.industry,
        generalNotes: data.general_notes,
        ...mapDbCustomerScalarFields(data),
        courseFee: data.course_fee ?? null,
        paymentOnTheDayType: data.payment_on_the_day_type || undefined,
        prePaymentAmount: data.pre_payment_amount ?? null,
        prepaymentPaymentType: data.prepayment_payment_type || undefined,
        bookedOn: data.booked_on || undefined,
        bookedBy: data.booked_by || undefined,
        mhSetUp: data.mh_set_up ?? undefined,
        invoiceNumber: data.invoice_number || undefined,
        otherInfoNotes: data.other_info_notes || undefined,
        ihascoCompleted: data.ihasco_completed ?? undefined,
        safePassDepositAmount: data.safe_pass_deposit_amount ?? null,
        safePassDepositType: data.safe_pass_deposit_type || undefined,
        safePassDepositDate: data.safe_pass_deposit_date || undefined,
        safePassManualHandling: data.safe_pass_manual_handling ?? undefined,
        safePassProcessNumber: data.safe_pass_process_number || undefined,
        safePassCardPostedOn: data.safe_pass_card_posted_on || undefined,
        safePassPostToAddress: data.safe_pass_post_to_address || undefined,
        safePassCardStatus: data.safe_pass_card_status || 'not_issued',
        locations: dedupeCustomerLocations((data.locations || []).map((loc: any) => mapDbLocationRow(loc))),
        contacts: (data.contacts || []).map((contact: any) => mapDbContactRow(contact)),
        numCertsGenerated: data.num_certs_generated || 0,
        isPaid: data.is_paid || false,
        lastPaymentDate: data.last_payment_date || '',
        paymentHistory: data.payment_history || [],
        currentBatchCertificates: data.current_batch_certificates || [],
        generatedCertificates: data.generated_certificates || []
      };
    } catch (error) {
      console.error('Error fetching customer by name:', error);
      return undefined;
    }
  },

  async getCustomerByNameOld(companyName: string): Promise<Customer | null> {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('companyName', companyName)
      .single();
    
    if (error && error.code !== 'PGRST116') throw error;
    return data || null;
  },

  async addCustomer(customer: Customer): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    const normalizedCustomer = normalizeCustomerNameFields(customer);

    if (await deferCustomerWriteToOfflineIfNeeded('create', normalizedCustomer)) {
      return;
    }

    try {
      const basePayload = {
        id: normalizedCustomer.id,
        tenant_id: DEFAULT_TENANT_ID,
        ...buildCustomerWritePayload(normalizedCustomer),
      };
      const financialPayload = buildCustomerFinancialPayload(normalizedCustomer);

      const { error } = await supabase.from('customers').insert({ ...basePayload, ...financialPayload });

      if (error) {
        console.error('Database error adding customer:', error);
        throw error;
      }

      // Invalidate customer cache
      const { localCustomerStorage } = await import('./localStorageCache');
      localCustomerStorage.clear();
      console.log('🗑️ Customer cache invalidated after add');
    } catch (error) {
      console.error('Error adding customer:', error);
      throw error;
    }
  },

  async addCustomerOld(customer: Customer): Promise<void> {
    const { error } = await supabase
      .from('customers')
      .insert([customer]);
    
    if (error) throw error;
  },

  async updateCustomer(customer: Customer): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    const normalizedCustomer = normalizeCustomerNameFields(customer);

    if (await deferCustomerWriteToOfflineIfNeeded('update', normalizedCustomer)) {
      return;
    }

    try {
      const basePayload = {
        ...buildCustomerWritePayload(normalizedCustomer),
        updated_at: new Date().toISOString(),
      };
      const financialPayload = buildCustomerFinancialPayload(normalizedCustomer);

      const { error } = await supabase
        .from('customers')
        .update({ ...basePayload, ...financialPayload })
        .eq('id', normalizedCustomer.id);

      if (error) {
        throw error;
      }

      // Invalidate customer cache
      const { localCustomerStorage } = await import('./localStorageCache');
      localCustomerStorage.clear();
      console.log('🗑️ Customer cache invalidated after update');
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  },

  async updateCustomerOld(customer: Customer): Promise<void> {
    const { error } = await supabase
      .from('customers')
      .update(customer)
      .eq('id', customer.id);
    
    if (error) throw error;
  },

  async deleteCustomer(id: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    if (await deferCustomerWriteToOfflineIfNeeded('delete', { id } as Customer)) {
      return;
    }

    try {
      const { data: customerRow, error: customerFetchError } = await supabase
        .from('customers')
        .select(
          'id, first_name, surname, company_name, customer_type, email, generated_certificates'
        )
        .eq('id', id)
        .maybeSingle();
      if (customerFetchError) throw customerFetchError;
      if (!customerRow) {
        const { localCustomerStorage } = await import('./localStorageCache');
        localCustomerStorage.clear();
        return;
      }

      const displayName = (
        `${String(customerRow.first_name || '').trim()} ${String(customerRow.surname || '').trim()}`.trim() ||
        String(customerRow.company_name || '').trim() ||
        id
      );

      const certNumbers: string[] = [];
      const certsRaw = Array.isArray(customerRow.generated_certificates)
        ? customerRow.generated_certificates
        : [];
      for (const c of certsRaw) {
        const num = String((c as { id?: string })?.id || '').trim();
        if (num) certNumbers.push(num);
      }

      const linkedSessionIds = new Set<string>();
      let linkedAttendees: Array<{
        id: string;
        booking_id?: string | null;
        company_customer_id?: string | null;
        sponsoring_company_customer_id?: string | null;
      }> = [];

      if (customerRow?.customer_type === 'individual') {
        const { data: attendees, error: attendeeFetchError } = await supabase
          .from('course_session_attendees')
          .select('id, booking_id, company_customer_id, sponsoring_company_customer_id')
          .eq('individual_customer_id', id)
          .eq('is_virtual', false);
        if (attendeeFetchError) throw attendeeFetchError;
        linkedAttendees = attendees || [];

        for (const attendee of linkedAttendees) {
          if (attendee.booking_id) linkedSessionIds.add(String(attendee.booking_id));
          let placeholderLabel = 'CRM deleted — assign participant';
          const companyId = attendee.company_customer_id || attendee.sponsoring_company_customer_id;
          if (companyId) {
            const { data: companyRow } = await supabase
              .from('customers')
              .select('company_name')
              .eq('id', companyId)
              .maybeSingle();
            const companyName = String(companyRow?.company_name || '').trim();
            if (companyName) placeholderLabel = companyName;
          }

          const { error: attendeeUpdateError } = await supabase
            .from('course_session_attendees')
            .update({
              enrollment_first_name: customerRow.first_name,
              enrollment_surname: customerRow.surname,
              individual_customer_id: null,
              is_virtual: true,
              placeholder_label: placeholderLabel,
            })
            .eq('id', attendee.id);
          if (attendeeUpdateError) throw attendeeUpdateError;
        }
      }

      // Soft-invalidate QR validation before hard-deleting the CRM row.
      try {
        const {
          revokeIssuedCertificatesForCustomer,
          revokeIssuedCertificatesByNumbers,
        } = await import('./issuedCertificatesService');
        const byCustomer = await revokeIssuedCertificatesForCustomer(
          id,
          `CRM participant deleted: ${displayName}`
        );
        const byNumber = await revokeIssuedCertificatesByNumbers(
          certNumbers,
          `CRM participant deleted: ${displayName}`
        );
        console.log(
          `🔒 Revoked issued certificates for deleted customer ${id}: customer=${byCustomer}, numbers=${byNumber}`
        );
      } catch (revokeErr) {
        console.error('Failed to revoke certificates on customer delete:', revokeErr);
        // Still proceed with delete — Bartek wants CRM delete to win; QR soft-revoke is best-effort.
      }

      // Audit trail (who / when / which record) — History currently missed deletes.
      try {
        const { AuditLogService, auditActions, auditResources } = await import('./auditLogService');
        const { data: authData } = await supabase.auth.getUser();
        const actor = authData?.user;
        const actorEmail = actor?.email || null;
        const actorNameMeta =
          actor?.user_metadata?.first_name && actor?.user_metadata?.last_name
            ? `${actor.user_metadata.first_name} ${actor.user_metadata.last_name}`.trim()
            : actorEmail;
        await AuditLogService.logAction(
          auditActions.CUSTOMER_DELETED,
          auditResources.CUSTOMERS,
          {
            customerId: id,
            customerType: customerRow.customer_type,
            displayName,
            email: customerRow.email || null,
            revokedCertificateNumbers: certNumbers,
            linkedSessionIds: Array.from(linkedSessionIds),
            actorName: actorNameMeta,
          },
          actor?.id,
          actorEmail || undefined
        );

        const { logAdminActivity } = await import('./adminActivityService');
        await logAdminActivity({
          actorUserId: actor?.id || null,
          actorName: actorNameMeta || null,
          eventType: 'customer_deleted',
          summary: `Deleted CRM participant ${displayName}`,
          details: {
            customerId: id,
            customerType: String(customerRow.customer_type || ''),
            displayName,
            certificateCount: certNumbers.length,
          },
        });

        const { courseSessionsService } = await import('./courseSessionsService');
        for (const sessionId of linkedSessionIds) {
          await courseSessionsService.logSessionActivity({
            sessionId,
            actorUserId: actor?.id || null,
            actorName: actorNameMeta || null,
            eventType: 'participant_crm_deleted',
            summary: `CRM participant deleted: ${displayName}`,
            details: {
              customerId: id,
              displayName,
              certificateCount: certNumbers.length,
            },
          });
        }
      } catch (auditErr) {
        console.error('Failed to write delete audit trail:', auditErr);
      }

      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      // Invalidate customer cache
      const { localCustomerStorage } = await import('./localStorageCache');
      localCustomerStorage.clear();
      console.log('🗑️ Customer cache invalidated after delete');
    } catch (error) {
      console.error('Error deleting customer:', error);
      throw error;
    }
  },

  async getExistingImportKeys(): Promise<{ sageCodes: Set<string>; companyNames: Set<string> }> {
    if (!isSupabaseConfigured()) {
      return { sageCodes: new Set(), companyNames: new Set() };
    }

    const sageCodes = new Set<string>();
    const companyNames = new Set<string>();
    let offset = 0;
    const pageSize = 1000;
    let hasMore = true;

    let includeSageCode = true;

    while (hasMore) {
      const selectFields = includeSageCode ? 'sage_code, company_name' : 'company_name';
      const { data, error } = await supabase
        .from('customers')
        .select(selectFields)
        .range(offset, offset + pageSize - 1);

      if (error) {
        if (includeSageCode) {
          includeSageCode = false;
          offset = 0;
          hasMore = true;
          continue;
        }
        throw error;
      }

      const rows = (data || []) as Array<{ sage_code?: string; company_name?: string }>;
      for (const row of rows) {
        if (includeSageCode) {
          const code = String(row.sage_code || '').trim();
          if (code) sageCodes.add(code.toLowerCase());
        }
        const name = normalizeCompanyNameForMatching(String(row.company_name || ''));
        if (name) companyNames.add(name);
      }

      hasMore = rows.length === pageSize;
      offset += pageSize;
    }

    return { sageCodes, companyNames };
  },

  async bulkImportCustomers(
    customers: Customer[],
    onProgress?: (done: number, total: number) => void
  ): Promise<{ customers: number; locations: number; contacts: number }> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    if (customers.length === 0) {
      return { customers: 0, locations: 0, contacts: 0 };
    }

    let importedCustomers = 0;
    let importedLocations = 0;
    let importedContacts = 0;
    const totalSteps = customers.length;
    let doneSteps = 0;

    const reportProgress = () => {
      doneSteps += 1;
      onProgress?.(doneSteps, totalSteps);
    };

    for (let i = 0; i < customers.length; i += BULK_IMPORT_BATCH_SIZE) {
      const batch = customers.slice(i, i + BULK_IMPORT_BATCH_SIZE).map((customer) =>
        normalizeCustomerNameFields(customer)
      );

      const customerRows = batch.map((customer) =>
        stripUndefinedFields({
          id: customer.id,
          tenant_id: DEFAULT_TENANT_ID,
          ...buildCustomerWritePayload(customer),
          ...buildCustomerFinancialPayload(customer),
        })
      );

      let { error: customerError } = await supabase.from('customers').insert(customerRows);

      // PostgREST schema cache may lag behind new sage import columns — retry without them.
      if (customerError && /sage_code|website|mobile_phone|customer_since|schema cache/i.test(formatSupabaseError(customerError))) {
        const fallbackRows = customerRows.map((row) => {
          const copy = { ...row };
          delete copy.sage_code;
          delete copy.website;
          delete copy.mobile_phone;
          delete copy.customer_since;
          return copy;
        });
        ({ error: customerError } = await supabase.from('customers').insert(fallbackRows));
      }

      if (customerError) {
        console.error('Bulk customer insert error:', customerError);
        throw new Error(formatSupabaseError(customerError));
      }
      importedCustomers += batch.length;

      const locationRows: Record<string, unknown>[] = [];
      const contactRows: Record<string, unknown>[] = [];

      for (const customer of batch) {
        for (const location of customer.locations || []) {
          locationRows.push({
            id: location.id,
            ...locationDbWritePayload(customer.id, location),
          });
        }
        for (const contact of customer.contacts || []) {
          const normalizedContact = normalizeContactFields(contact);
          if (!normalizedContact.email?.trim()) continue;
          contactRows.push({
            id: normalizedContact.id,
            ...contactDbWritePayload(customer.id, normalizedContact),
          });
        }
      }

      for (let j = 0; j < locationRows.length; j += BULK_IMPORT_BATCH_SIZE) {
        const chunk = locationRows.slice(j, j + BULK_IMPORT_BATCH_SIZE);
        if (chunk.length === 0) continue;
        const { error } = await supabase.from('locations').insert(chunk);
        if (error) {
          console.error('Bulk location insert error:', error);
          throw new Error(formatSupabaseError(error));
        }
        importedLocations += chunk.length;
      }

      for (let j = 0; j < contactRows.length; j += BULK_IMPORT_BATCH_SIZE) {
        const chunk = contactRows.slice(j, j + BULK_IMPORT_BATCH_SIZE);
        if (chunk.length === 0) continue;
        const { error } = await supabase.from('contacts').insert(chunk);
        if (error) {
          console.error('Bulk contact insert error:', error);
          throw new Error(formatSupabaseError(error));
        }
        importedContacts += chunk.length;
      }

      batch.forEach(() => reportProgress());
    }

    const { localCustomerStorage } = await import('./localStorageCache');
    localCustomerStorage.clear();

    return {
      customers: importedCustomers,
      locations: importedLocations,
      contacts: importedContacts,
    };
  },

  async deleteCustomerOld(id: string): Promise<void> {
    const { error } = await supabase
      .from('customers')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// Location Storage
export const supabaseLocationStorage = {
  async getLocations(): Promise<Location[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .order('created_at', { ascending: true });
      
      if (error) {
        throw error;
      }

      return (data || []).map((loc) => mapDbLocationRow(loc));
    } catch (error) {
      console.error('Error fetching locations:', error);
      return [];
    }
  },

  async addLocation(customerId: string, location: Location): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    if (await deferLocationWriteToOfflineIfNeeded('create', customerId, location)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('locations')
        .insert({
          id: location.id,
          ...locationDbWritePayload(customerId, location),
        });
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error adding location:', error);
      throw error;
    }
  },

  async updateLocation(customerId: string, location: Location): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    if (await deferLocationWriteToOfflineIfNeeded('update', customerId, location)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('locations')
        .update(locationDbWritePayload(customerId, location))
        .eq('id', location.id)
        .eq('customer_id', customerId);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error updating location:', error);
      throw error;
    }
  },

  async deleteLocation(customerId: string, locationId: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    if (await deferLocationWriteToOfflineIfNeeded('delete', customerId, { id: locationId } as Location)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('locations')
        .delete()
        .eq('id', locationId)
        .eq('customer_id', customerId);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error deleting location:', error);
      throw error;
    }
  },

  async getLocationsOld(): Promise<Location[]> {
    const { data, error } = await supabase
      .from('locations')
      .select('*')
      .order('name');
    
    if (error) throw error;
    return data || [];
  },

  async addLocationOld(location: Location): Promise<void> {
    const { error } = await supabase
      .from('locations')
      .insert([location]);
    
    if (error) throw error;
  },

  async updateLocationOld(location: Location): Promise<void> {
    const { error } = await supabase
      .from('locations')
      .update(location)
      .eq('id', location.id);
    
    if (error) throw error;
  },

  async deleteLocationOld(id: string): Promise<void> {
    const { error } = await supabase
      .from('locations')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  async getLocationById(locationId: string): Promise<Location | null> {
    if (!isSupabaseConfigured()) {
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('id', locationId)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }

      if (!data) return null;

      return mapDbLocationRow(data);
    } catch (error) {
      console.error('Error fetching location by ID:', error);
      return null;
    }
  },

  /** Fetch multiple locations by ID (e.g. for Ops when customer list doesn't include them). */
  async getLocationsByIds(ids: string[]): Promise<Location[]> {
    if (!isSupabaseConfigured() || !ids.length) {
      return [];
    }
    const unique = [...new Set(ids.filter(Boolean))];
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .in('id', unique);
      if (error) throw error;
      return (data || []).map((loc: any) => mapDbLocationRow(loc));
    } catch (error) {
      console.error('Error fetching locations by IDs:', error);
      return [];
    }
  }
};

// Contact Storage
export const supabaseContactStorage = {
  async getContacts(): Promise<Contact[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .order('first_name');
      
      if (error) {
        throw error;
      }

      return (data || []).map((contact) => mapDbContactRow(contact));
    } catch (error) {
      console.error('Error fetching contacts:', error);
      return [];
    }
  },

  async addContact(customerId: string, contact: Contact): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    const normalizedContact = normalizeContactFields(contact);

    if (await deferContactWriteToOfflineIfNeeded('create', customerId, normalizedContact)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('contacts')
        .insert({
          id: normalizedContact.id,
          ...contactDbWritePayload(customerId, normalizedContact),
        });
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error adding contact:', error);
      throw error;
    }
  },

  async updateContact(customerId: string, contact: Contact): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    const normalizedContact = normalizeContactFields(contact);

    if (await deferContactWriteToOfflineIfNeeded('update', customerId, normalizedContact)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('contacts')
        .update(contactDbWritePayload(customerId, normalizedContact))
        .eq('id', normalizedContact.id)
        .eq('customer_id', customerId);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      throw error;
    }
  },

  async deleteContact(customerId: string, contactId: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    if (await deferContactWriteToOfflineIfNeeded('delete', customerId, { id: contactId } as Contact)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('contacts')
        .delete()
        .eq('id', contactId)
        .eq('customer_id', customerId);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      throw error;
    }
  },

  async getContactById(contactId: string): Promise<Contact | null> {
    if (!isSupabaseConfigured()) {
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('id', contactId)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }

      if (!data) return null;

      return mapDbContactRow(data);
    } catch (error) {
      console.error('Error fetching contact by ID:', error);
      return null;
    }
  },

  async getContactsOld(): Promise<Contact[]> {
    const { data, error } = await supabase
      .from('contacts')
      .select('*')
      .order('name');
    
    if (error) throw error;
    return data || [];
  },

  async addContactOld(contact: Contact): Promise<void> {
    const { error } = await supabase
      .from('contacts')
      .insert([contact]);
    
    if (error) throw error;
  },

  async updateContactOld(contact: Contact): Promise<void> {
    const { error } = await supabase
      .from('contacts')
      .update(contact)
      .eq('id', contact.id);
    
    if (error) throw error;
  },

  async deleteContactOld(id: string): Promise<void> {
    const { error } = await supabase
      .from('contacts')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// Email Template Storage
export const supabaseEmailTemplateStorage = {
  async getEmailTemplates(): Promise<EmailTemplate[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .order('name');
      
      if (error) {
        throw error;
      }

      return (data || []).map(template => ({
        id: template.id,
        name: template.name,
        subject: template.subject,
        body: template.body,
        isDefault: template.is_default
      }));
    } catch (error) {
      console.error('Error fetching email templates:', error);
      return [];
    }
  },

  async addEmailTemplate(template: EmailTemplate): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      const { error } = await supabase
        .from('email_templates')
        .insert({
          id: template.id,
          name: template.name,
          subject: template.subject,
          body: template.body,
          is_default: template.isDefault
        });
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error adding email template:', error);
      throw error;
    }
  },

  async updateEmailTemplate(template: EmailTemplate): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      const { error } = await supabase
        .from('email_templates')
        .update({
          name: template.name,
          subject: template.subject,
          body: template.body,
          is_default: template.isDefault,
          updated_at: new Date().toISOString()
        })
        .eq('id', template.id);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error updating email template:', error);
      throw error;
    }
  },

  async deleteEmailTemplate(id: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      const { error } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', id);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error deleting email template:', error);
      throw error;
    }
  },

  async getEmailTemplatesOld(): Promise<EmailTemplate[]> {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .order('name');
    
    if (error) throw error;
    return data || [];
  },

  async addEmailTemplateOld(template: EmailTemplate): Promise<void> {
    const { error } = await supabase
      .from('email_templates')
      .insert([template]);
    
    if (error) throw error;
  },

  async updateEmailTemplateOld(template: EmailTemplate): Promise<void> {
    const { error } = await supabase
      .from('email_templates')
      .update(template)
      .eq('id', template.id);
    
    if (error) throw error;
  },

  async deleteEmailTemplateOld(id: string): Promise<void> {
    const { error } = await supabase
      .from('email_templates')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// WhatsApp Template Storage (plain-text bodies; {{placeholders}} aligned with email templates)
export const supabaseWhatsAppTemplateStorage = {
  async getWhatsAppTemplates(): Promise<WhatsAppTemplate[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      const { data, error } = await supabase.from('whatsapp_templates').select('*').order('name');

      if (error) {
        throw error;
      }

      return (data || []).map((template: Record<string, unknown>) => ({
        id: template.id as string,
        name: template.name as string,
        body: template.body as string,
        isDefault: Boolean(template.is_default),
      }));
    } catch (error) {
      console.error('Error fetching WhatsApp templates:', error);
      return [];
    }
  },

  async addWhatsAppTemplate(template: WhatsAppTemplate): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    const { error } = await supabase.from('whatsapp_templates').insert({
      id: template.id,
      name: template.name,
      body: template.body,
      is_default: template.isDefault,
    });

    if (error) throw error;
  },

  async updateWhatsAppTemplate(template: WhatsAppTemplate): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    const { error } = await supabase
      .from('whatsapp_templates')
      .update({
        name: template.name,
        body: template.body,
        is_default: template.isDefault,
        updated_at: new Date().toISOString(),
      })
      .eq('id', template.id);

    if (error) throw error;
  },

  async deleteWhatsAppTemplate(id: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    const { error } = await supabase.from('whatsapp_templates').delete().eq('id', id);

    if (error) throw error;
  },
};

const certificateRemindersCache = createRequestCache<ReminderCert[]>(90_000);

export function invalidateCertificateRemindersCache(): void {
  certificateRemindersCache.invalidate();
}

// Reminder Storage
export const supabaseReminderStorage = {
  async getReminders(options?: { force?: boolean }): Promise<ReminderCert[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    return certificateRemindersCache.get(async () => {
      const { data, error } = await supabase
        .from('certificate_reminders')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || []).map((reminder) => ({
        id: reminder.id,
        customerId: reminder.customer_id,
        companyCustomerId: reminder.company_customer_id || reminder.customer_id || undefined,
        companyName: reminder.company_name,
        attendeeName: reminder.attendee_name,
        courseName: reminder.course_name,
        courseDate: reminder.course_date,
        courseDates:
          reminder.course_dates != null
            ? Array.isArray(reminder.course_dates)
              ? reminder.course_dates
              : [reminder.course_dates]
            : undefined,
        expiryDate: reminder.expiry_date,
        isRenewed: reminder.is_renewed,
        markedRenewedDate: reminder.marked_renewed_date,
        lastReminderSent: reminder.last_reminder_sent,
      }));
    }, options);
  },

  async getExpiringCount(days: number = 30): Promise<number> {
    if (!isSupabaseConfigured()) {
      return 0;
    }

    try {
      const reminders = await this.getReminders();
      const today = new Date();
      const futureDate = new Date();
      futureDate.setDate(today.getDate() + days);

      return reminders.filter(reminder => {
        if (reminder.isRenewed) return false;
        if (reminder.expiryDate === 'Invalid Date') return false;
        
        // Try parsing as ISO format first (YYYY-MM-DD)
        if (reminder.expiryDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const expiryDate = new Date(reminder.expiryDate);
          if (isNaN(expiryDate.getTime())) return false;
          return expiryDate >= today && expiryDate <= futureDate;
        }
        
        // Parse DD-MM-YYYY format (legacy)
        const parts = reminder.expiryDate.split('-');
        if (parts.length !== 3) return false;
        
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const year = parseInt(parts[2], 10);
        const expiryDate = new Date(year, month, day);
        
        if (isNaN(expiryDate.getTime())) return false;
        
        return expiryDate >= today && expiryDate <= futureDate;
      }).length;
    } catch (error) {
      console.error('Error getting expiring count:', error);
      return 0;
    }
  },

  async addReminder(reminder: ReminderCert): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      const { error } = await supabase
        .from('certificate_reminders')
        .insert({
          id: reminder.id,
          customer_id: reminder.customerId,
          company_customer_id: reminder.companyCustomerId || reminder.customerId || null,
          company_name: reminder.companyName,
          attendee_name: reminder.attendeeName,
          course_name: reminder.courseName,
          course_date: reminder.courseDate,
          course_dates: reminder.courseDates && reminder.courseDates.length > 0 ? reminder.courseDates : null,
          expiry_date: reminder.expiryDate,
          is_renewed: reminder.isRenewed,
          marked_renewed_date: reminder.markedRenewedDate,
          last_reminder_sent: reminder.lastReminderSent
        });
      
      if (error) {
        throw error;
      }
      invalidateCertificateRemindersCache();
    } catch (error) {
      console.error('Error adding reminder:', error);
      throw error;
    }
  },

  async updateReminder(reminder: ReminderCert): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      const { error } = await supabase
        .from('certificate_reminders')
        .update({
          customer_id: reminder.customerId,
          company_customer_id: reminder.companyCustomerId || reminder.customerId || null,
          company_name: reminder.companyName,
          attendee_name: reminder.attendeeName,
          course_name: reminder.courseName,
          course_date: reminder.courseDate,
          course_dates: reminder.courseDates && reminder.courseDates.length > 0 ? reminder.courseDates : null,
          expiry_date: reminder.expiryDate,
          is_renewed: reminder.isRenewed,
          marked_renewed_date: reminder.markedRenewedDate,
          last_reminder_sent: reminder.lastReminderSent,
          updated_at: new Date().toISOString()
        })
        .eq('id', reminder.id);
      
      if (error) {
        throw error;
      }
      invalidateCertificateRemindersCache();
    } catch (error) {
      console.error('Error updating reminder:', error);
      throw error;
    }
  },

  async deleteReminder(id: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      const { error } = await supabase
        .from('certificate_reminders')
        .delete()
        .eq('id', id);
      
      if (error) {
        throw error;
      }
      invalidateCertificateRemindersCache();
    } catch (error) {
      console.error('Error deleting reminder:', error);
      throw error;
    }
  },

  async getRemindersOld(): Promise<ReminderCert[]> {
    const { data, error } = await supabase
      .from('reminders')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async addReminderOld(reminder: ReminderCert): Promise<void> {
    const { error } = await supabase
      .from('reminders')
      .insert([reminder]);
    
    if (error) throw error;
  },

  async updateReminderOld(reminder: ReminderCert): Promise<void> {
    const { error } = await supabase
      .from('reminders')
      .update(reminder)
      .eq('id', reminder.id);
    
    if (error) throw error;
  },

  async deleteReminderOld(id: string): Promise<void> {
    const { error } = await supabase
      .from('reminders')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// Review Storage
export const supabaseReviewStorage = {
  async getReviewReminders(): Promise<ReviewReminder[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('review_reminders')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }

      const reviewReminders = (data || []).map(reminder => ({
        id: reminder.id,
        customerId: reminder.customer_id,
        companyName: reminder.company_name,
        completionDate: reminder.completion_date,
        payment_date: reminder.payment_date,
        dueReminderDate: reminder.due_reminder_date,
        isSent: reminder.is_sent,
        isCompleted: reminder.is_completed,
        lastAttemptDate: reminder.last_attempt_date,
        bookingId: reminder.booking_id
      }));

      return reviewReminders;
    } catch (error: any) {
      console.error('Error fetching review reminders:', error);
      
      // Check if this is a network/service error (not auth error)
      const isNetworkError = error?.message?.includes('Failed to fetch') || 
                            error?.message?.includes('NetworkError') ||
                            error?.code === 'ECONNREFUSED' ||
                            error?.code === 'ETIMEDOUT';
      
      if (isNetworkError) {
        console.log('⚠️ Network error fetching review reminders - returning empty array');
      }
      
      return [];
    }
  },

  async getDueTodayCount(): Promise<number> {
    if (!isSupabaseConfigured()) {
      return 0;
    }

    try {
      const reminders = await this.getReviewReminders();
      const today = new Date().toISOString().split('T')[0];
      
      return reminders.filter(reminder => 
        !reminder.isSent && 
        !reminder.isCompleted && 
        reminder.dueReminderDate <= today
      ).length;
    } catch (error) {
      console.error('Error getting due today count:', error);
      return 0;
    }
  },

  async addReviewReminder(reminder: ReviewReminder): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      const { error } = await supabase
        .from('review_reminders')
        .insert({
          id: reminder.id,
          customer_id: reminder.customerId,
          company_name: reminder.companyName,
          payment_date: reminder.payment_date || reminder.completionDate, // Use payment_date if available, fallback to completionDate
          due_reminder_date: reminder.dueReminderDate,
          is_sent: reminder.isSent,
          is_completed: reminder.isCompleted,
          last_attempt_date: reminder.lastAttemptDate,
          booking_id: reminder.bookingId
        });
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error adding review reminder:', error);
      throw error;
    }
  },

  async updateReviewReminder(reminder: ReviewReminder): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      const { error } = await supabase
        .from('review_reminders')
        .update({
          customer_id: reminder.customerId,
          company_name: reminder.companyName,
          payment_date: reminder.payment_date || reminder.completionDate, // Use payment_date if available, fallback to completionDate
          due_reminder_date: reminder.dueReminderDate,
          is_sent: reminder.isSent,
          is_completed: reminder.isCompleted,
          last_attempt_date: reminder.lastAttemptDate,
          booking_id: reminder.bookingId,
          updated_at: new Date().toISOString()
        })
        .eq('id', reminder.id);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error updating review reminder:', error);
      throw error;
    }
  },

  async deleteReviewReminder(id: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      const { error } = await supabase
        .from('review_reminders')
        .delete()
        .eq('id', id);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error deleting review reminder:', error);
      throw error;
    }
  },

  async getGoogleReviewLink(): Promise<string> {
    if (!isSupabaseConfigured()) {
      return '';
    }

    try {
      const { data, error } = await supabase
        .from('tenant_settings')
        .select('setting_value')
        .eq('tenant_id', DEFAULT_TENANT_ID)
        .eq('setting_key', 'google_review_link')
        .maybeSingle();
      
      if (error) {
        throw error;
      }
      
      const value = data?.setting_value;
      return typeof value === 'string' ? value : '';
    } catch (error) {
      console.error('Error fetching Google review link:', error);
      return '';
    }
  },

  async setGoogleReviewLink(link: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      const { error } = await supabase
        .from('tenant_settings')
        .upsert(
          {
            tenant_id: DEFAULT_TENANT_ID,
            setting_key: 'google_review_link',
            setting_value: link,
            updated_at: new Date().toISOString()
          },
          { onConflict: 'tenant_id,setting_key' }
        );
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error setting Google review link:', error);
      throw error;
    }
  },

  async getCertificateCounter(): Promise<number> {
    if (!isSupabaseConfigured()) {
      return 1000; // Default fallback
    }

    try {
      const { data, error } = await supabase
        .from('tenant_settings')
        .select('setting_value')
        .eq('tenant_id', DEFAULT_TENANT_ID)
        .eq('setting_key', 'certificate_counter')
        .maybeSingle();

      if (error) {
        if (error.code !== 'PGRST116') {
          console.error('Error fetching certificate counter:', error);
        }
        return 1000;
      }

      if (!data) {
        return 1000;
      }

      const raw = data.setting_value;
      const n =
        typeof raw === 'number'
          ? raw
          : typeof raw === 'string'
            ? Number.parseInt(raw, 10)
            : Number.parseInt(String(raw ?? ''), 10);
      return Number.isFinite(n) && n > 0 ? n : 1000;
    } catch (error) {
      console.error('Error fetching certificate counter:', error);
      return 1000; // Default fallback
    }
  },

  async getHighestIssuedCertificateCounter(): Promise<number> {
    if (!isSupabaseConfigured()) {
      return 999;
    }

    try {
      let highestNumber = 999;
      const pageSize = 500;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('customers')
          .select('generated_certificates')
          .range(offset, offset + pageSize - 1);

        if (error) {
          throw error;
        }

        const rows = data || [];
        rows.forEach((customer: { generated_certificates?: unknown[] }) => {
          const certs = Array.isArray(customer.generated_certificates) ? customer.generated_certificates : [];
          certs.forEach((cert) => {
            if (!cert || typeof cert !== 'object') return;
            const certId = String((cert as { id?: unknown }).id || '');
            const match = certId.match(/^(?:TT|SEQ|CTS)-(\d+)$/i);
            if (!match) return;
            const number = Number.parseInt(match[1], 10);
            if (Number.isFinite(number) && number > highestNumber) {
              highestNumber = number;
            }
          });
        });

        hasMore = rows.length === pageSize;
        offset += pageSize;
      }

      return highestNumber;
    } catch (error) {
      console.error('Error finding highest issued certificate number:', error);
      return 999;
    }
  },

  async incrementCertificateCounter(): Promise<number> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      // Keep the counter aligned with already-issued certificate IDs.
      const [currentCounter, highestIssuedCounter] = await Promise.all([
        this.getCertificateCounter(),
        this.getHighestIssuedCertificateCounter(),
      ]);
      const baseline = Math.max(currentCounter, highestIssuedCounter);
      const newCounter = baseline + 1;

      // Upsert avoids silent no-op when tenant_settings row does not exist yet.
      const { error } = await supabase
        .from('tenant_settings')
        .upsert(
          {
            tenant_id: DEFAULT_TENANT_ID,
            setting_key: 'certificate_counter',
            setting_value: newCounter,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'tenant_id,setting_key' }
        );
      
      if (error) {
        throw error;
      }

      return newCounter;
    } catch (error) {
      console.error('Error incrementing certificate counter:', error);
      throw error;
    }
  },

  async getReviewsOld(): Promise<ReviewReminder[]> {
    const { data, error } = await supabase
      .from('reviews')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async addReviewOld(review: ReviewReminder): Promise<void> {
    const { error } = await supabase
      .from('reviews')
      .insert([review]);
    
    if (error) throw error;
  },

  async updateReviewOld(review: ReviewReminder): Promise<void> {
    const { error } = await supabase
      .from('reviews')
      .update(review)
      .eq('id', review.id);
    
    if (error) throw error;
  },

  async deleteReviewOld(id: string): Promise<void> {
    const { error } = await supabase
      .from('reviews')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// Booking Storage
export const supabaseBookingStorage = {
  async getBookings(limit?: number, offset?: number): Promise<Booking[]> {
    if (!isSupabaseConfigured()) {
      // Try to return cached data even if not configured
      const { localBookingStorage } = await import('./localStorageCache');
      const cachedData = localBookingStorage.getData<Booking>();
      if (cachedData && cachedData.length > 0) {
        console.log('⚠️ Using cached booking data (Supabase not configured)');
        return cachedData;
      }
      return [];
    }

    // Import cache at function level to avoid circular dependencies
    const { localBookingStorage } = await import('./localStorageCache');

    try {
      let query = supabase
        .from('bookings')
        .select(`
          *,
          booking_dates(date, start_time, end_time)
        `)
        .order('created_at', { ascending: false });
      
      // Apply pagination if provided
      if (limit !== undefined) {
        query = query.limit(limit);
      }
      if (offset !== undefined) {
        query = query.range(offset, offset + (limit || 1000) - 1);
      }
      
      const { data, error } = await query;
      
      if (error) {
        throw error;
      }
      
      // Fetch attendees and public booking clients for all bookings in batches to avoid ERR_INSUFFICIENT_RESOURCES
      const bookings = data || [];
      
      // Extract all booking IDs for batch fetching
      const bookingIds = bookings.map((b: any) => b.id);
      
      // Batch fetch all attendees and public booking clients at once (prevents N+1 queries)
      const [attendeesMap, clientsMap] = await Promise.all([
        supabaseBookingAttendeesStorage.getBookingAttendeesBatch(bookingIds),
        supabasePublicBookingClientsStorage.getPublicBookingClientsBatch(bookingIds)
      ]);
      
      // Map bookings with their attendees and clients
      const bookingsWithAttendees = bookings.map((booking: any) => {
        const attendees = attendeesMap.get(booking.id) || [];
        const publicBookingClients = clientsMap.get(booking.id) || undefined;
        const isPublicByType = booking.booking_type === 'public';
        const hasNoCustomer = !booking.customer_id;
        
        // Determine booking type: if it's set to 'public' OR if it has public booking clients OR if customer_id is null
        const detectedBookingType = hasNoCustomer || isPublicByType || (publicBookingClients && publicBookingClients.length > 0) 
          ? 'public' 
          : 'standard';
        
        return {
            id: booking.id,
            customerId: booking.customer_id,
            payerCustomerId: booking.payer_customer_id || booking.customer_id || null,
            payerType: booking.payer_type || (booking.payer_customer_id ? 'company' : null),
            locationId: booking.location_id,
            contactId: booking.contact_id,
            instructorId: booking.instructor_id,
            courseName: booking.course_name,
            courseDate: booking.course_date,
            startTime: booking.start_time,
            endTime: booking.end_time,
            totalPrice: booking.total_price,
            instructorPrice: booking.instructor_price,
            instructorPO: booking.instructor_po,
            clientPO: booking.client_po,
            attendeeCount: booking.attendee_count,
          attendees: attendees.length > 0 ? attendees : undefined,
          notes: booking.notes,
          status: booking.status,
          bookingType: detectedBookingType,
          publicBookingClients: publicBookingClients,
          createdAt: booking.created_at,
          updatedAt: booking.updated_at,
          allBookingDates: (() => {
            const mainDate = booking.course_date;
            const additionalDates = booking.booking_dates?.map((bd: any) => bd.date) || [];

            // Combine and deduplicate all dates - ensure we filter out any falsy values
            const allDates = mainDate ? [mainDate, ...additionalDates] : additionalDates;
            const uniqueDates = [...new Set(allDates.filter(d => d))].sort();

            // Booking dates construction - log removed to reduce console noise
            return uniqueDates;
          })(),
          allBookingDatesWithTimes: (() => {
            const mainDate = booking.course_date;
            const mainStartTime = booking.start_time || '09:00';
            const mainEndTime = booking.end_time || '';
            
            // Create main date entry
            const mainDateEntry = mainDate ? [{
              date: mainDate,
              startTime: mainStartTime,
              endTime: mainEndTime
            }] : [];
            
            // Create additional dates entries
            const additionalDatesEntries = booking.booking_dates?.map((bd: any) => ({
              date: bd.date,
              startTime: bd.start_time || '09:00',
              endTime: bd.end_time || ''
            })) || [];
            
            // Combine and deduplicate by date, prioritizing entries with actual times
            const allEntries = [...mainDateEntry, ...additionalDatesEntries];
            
            // Remove duplicates by date, keeping the entry with the most specific time (not null/default)
            const dateMap = new Map();
            allEntries.forEach(entry => {
              const existing = dateMap.get(entry.date);
              if (!existing || (entry.startTime !== '09:00' && existing.startTime === '09:00')) {
                dateMap.set(entry.date, entry);
              }
            });
            
            const uniqueEntries = Array.from(dateMap.values());
            
            const result = uniqueEntries.sort((a, b) => a.date.localeCompare(b.date));
            
            
            return result;
          })()
        };
      });
      
      // Cache the booking data
      localBookingStorage.setData(bookingsWithAttendees);
      localBookingStorage.updateLastSync();
      
      return bookingsWithAttendees;
    } catch (error: any) {
      console.error('Error fetching bookings:', error);
      
      // Check if this is a network/service error (not auth error)
      const isNetworkError = error?.message?.includes('Failed to fetch') || 
                            error?.message?.includes('NetworkError') ||
                            error?.code === 'ECONNREFUSED' ||
                            error?.code === 'ETIMEDOUT';
      
      // Try to return cached data (even if stale) during network outages
      if (isNetworkError && localBookingStorage.hasData()) {
        const cachedData = localBookingStorage.getData<Booking>();
        if (cachedData && cachedData.length > 0) {
          console.log('⚠️ Using stale cached booking data due to network error');
          return cachedData;
        }
      }
      
      return [];
    }
  },

  /**
   * All bookings with columns needed for admin notification heuristics only.
   * Avoids nested booking_dates and avoids batch-loading attendees/public clients.
   */
  async getBookingsForAdminNotifications(): Promise<Booking[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    const selectCols =
      'id, customer_id, payer_customer_id, payer_type, contact_id, location_id, instructor_id, course_name, course_date, start_time, end_time, total_price, instructor_price, instructor_po, client_po, attendee_count, notes, status, booking_type, created_at, updated_at';

    try {
      const out: Booking[] = [];
      const pageSize = 800;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('bookings')
          .select(selectCols)
          .order('created_at', { ascending: false })
          .range(offset, offset + pageSize - 1);

        if (error) throw error;
        const rows = data || [];

        for (const booking of rows) {
          const isPublicByType = booking.booking_type === 'public';
          const hasNoCustomer = !booking.customer_id;
          const detectedBookingType = hasNoCustomer || isPublicByType ? 'public' : 'standard';

          out.push({
            id: booking.id,
            customerId: booking.customer_id,
            payerCustomerId: booking.payer_customer_id || booking.customer_id || null,
            payerType: booking.payer_type || (booking.payer_customer_id ? 'company' : null),
            locationId: booking.location_id,
            contactId: booking.contact_id,
            instructorId: booking.instructor_id,
            courseName: booking.course_name,
            courseDate: booking.course_date,
            startTime: booking.start_time,
            endTime: booking.end_time,
            totalPrice: booking.total_price,
            instructorPrice: booking.instructor_price,
            instructorPO: booking.instructor_po,
            clientPO: booking.client_po,
            attendeeCount: booking.attendee_count ?? 0,
            notes: booking.notes,
            status: booking.status,
            bookingType: detectedBookingType,
            publicBookingClients: undefined,
            createdAt: booking.created_at,
            updatedAt: booking.updated_at,
            allBookingDates: booking.course_date ? [booking.course_date] : [],
            allBookingDatesWithTimes: []
          } as Booking);
        }

        hasMore = rows.length === pageSize;
        offset += pageSize;
      }

      return out;
    } catch (error) {
      console.error('Error in getBookingsForAdminNotifications:', error);
      return [];
    }
  },

  // Fetch bookings in a date range (uses course_date for range filtering)
  async getLastBookingDateByCustomer(): Promise<Map<string, string>> {
    if (!isSupabaseConfigured()) return new Map();
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('customer_id, course_date')
        .not('customer_id', 'is', null)
        .neq('status', 'cancelled')
        .order('course_date', { ascending: false });
      if (error) throw error;
      const map = new Map<string, string>();
      (data || []).forEach((row: any) => {
        if (row.customer_id && !map.has(row.customer_id)) {
          map.set(row.customer_id, row.course_date);
        }
      });
      return map;
    } catch (e) {
      console.error('Error loading last booking dates:', e);
      return new Map();
    }
  },

  async getBookingsByDateRange(startDate: string, endDate: string): Promise<Booking[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    const mapOne = (booking: any, attendeesMap: Map<string, any[]>, clientsMap: Map<string, any>) => {
      const attendees = attendeesMap.get(booking.id) || [];
      const publicBookingClients = clientsMap.get(booking.id) || undefined;
      const isPublicByType = booking.booking_type === 'public';
      const detectedBookingType = isPublicByType || (publicBookingClients && publicBookingClients.length > 0)
        ? 'public'
        : 'standard';
      return {
        id: booking.id,
        customerId: booking.customer_id,
        payerCustomerId: booking.payer_customer_id || booking.customer_id || null,
        payerType: booking.payer_type || (booking.payer_customer_id ? 'company' : null),
        locationId: booking.location_id,
        contactId: booking.contact_id,
        instructorId: booking.instructor_id,
        courseName: booking.course_name,
        courseDate: booking.course_date,
        startTime: booking.start_time,
        endTime: booking.end_time,
        totalPrice: booking.total_price,
        instructorPrice: booking.instructor_price,
        instructorPO: booking.instructor_po,
        clientPO: booking.client_po,
        attendeeCount: booking.attendee_count,
        attendees: attendees.length > 0 ? attendees : undefined,
        notes: booking.notes,
        status: booking.status,
        bookingType: detectedBookingType,
        publicBookingClients: publicBookingClients,
        createdAt: booking.created_at,
        updatedAt: booking.updated_at,
        allBookingDates: booking.booking_dates?.map((d: any) => d.date).filter(Boolean) || [],
        allBookingDatesWithTimes: booking.booking_dates?.map((d: any) => ({
          date: d.date,
          startTime: d.start_time,
          endTime: d.end_time
        })) || []
      };
    };

    try {
      // 1) Bookings whose course_date is in range
      const { data: byCourseDate, error: err1 } = await supabase
        .from('bookings')
        .select(`
          *,
          booking_dates(date, start_time, end_time)
        `)
        .gte('course_date', startDate)
        .lte('course_date', endDate)
        .order('course_date', { ascending: true });

      if (err1) throw err1;
      const bookings = byCourseDate || [];
      const seenIds = new Set(bookings.map((b: any) => b.id));

      // 2) Bookings that have any booking_dates in range but course_date outside (e.g. multi-day that started earlier)
      const { data: datesInRange } = await supabase
        .from('booking_dates')
        .select('booking_id')
        .gte('date', startDate)
        .lte('date', endDate);
      const extraIds = (datesInRange || [])
        .map((r: any) => r.booking_id as string)
        .filter((id: string) => !seenIds.has(id));
      const uniqueExtraIds = [...new Set(extraIds)];

      let allRows: any[] = [...bookings];
      if (uniqueExtraIds.length > 0) {
        const { data: extraBookings, error: err2 } = await supabase
          .from('bookings')
          .select(`
            *,
            booking_dates(date, start_time, end_time)
          `)
          .in('id', uniqueExtraIds);
        if (!err2 && extraBookings?.length) {
          allRows = [...allRows, ...extraBookings];
        }
      }

      const allIds = allRows.map((b: any) => b.id);
      const [attendeesMap, clientsMap] = await Promise.all([
        supabaseBookingAttendeesStorage.getBookingAttendeesBatch(allIds),
        supabasePublicBookingClientsStorage.getPublicBookingClientsBatch(allIds)
      ]);

      const result = allRows.map((b: any) => mapOne(b, attendeesMap, clientsMap));
      result.sort((a, b) => (a.courseDate || '').localeCompare(b.courseDate || ''));
      return result;
    } catch (error: any) {
      console.error('Error fetching bookings by date range:', error);
      return [];
    }
  },

  // Server-side booking search (lightweight fields + PO / notes for global search)
  async searchBookings(
    term: string,
    limit: number = 10
  ): Promise<
    Array<
      Pick<Booking, 'id' | 'courseName' | 'courseDate' | 'status'> & {
        clientPO?: string;
        instructorPO?: string;
        notes?: string;
      }
    >
  > {
    if (!isSupabaseConfigured()) {
      return [];
    }

    const trimmed = term.trim();
    if (!trimmed) {
      return [];
    }

    // Commas break PostgREST `.or()` argument parsing
    const safe = trimmed.replace(/,/g, ' ');
    const uuidLike =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(safe);

    try {
      let query = supabase
        .from('bookings')
        .select('id, course_name, course_date, status, client_po, instructor_po, notes')
        .order('course_date', { ascending: false })
        .limit(limit);

      const orClause = uuidLike
        ? `course_name.ilike.%${safe}%,status.ilike.%${safe}%,notes.ilike.%${safe}%,client_po.ilike.%${safe}%,instructor_po.ilike.%${safe}%,id.eq.${safe}`
        : `course_name.ilike.%${safe}%,status.ilike.%${safe}%,notes.ilike.%${safe}%,client_po.ilike.%${safe}%,instructor_po.ilike.%${safe}%`;

      const { data, error } = await query.or(orClause);

      if (error) {
        throw error;
      }

      return (data || []).map((booking: any) => ({
        id: booking.id,
        courseName: booking.course_name,
        courseDate: booking.course_date,
        status: booking.status,
        clientPO: booking.client_po || undefined,
        instructorPO: booking.instructor_po || undefined,
        notes: booking.notes || undefined
      }));
    } catch (error) {
      console.error('Error searching bookings:', error);
      return [];
    }
  },

  async getBookingById(id: string): Promise<Booking | null> {
    if (!isSupabaseConfigured()) {
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          booking_dates(date, start_time, end_time)
        `)
        .eq('id', id)
        .single();
      
      if (error) {
        throw error;
      }
      
      if (!data) {
        return null;
      }
      
      // Fetch attendees separately
      const attendees = await supabaseBookingAttendeesStorage.getBookingAttendees(id);
      
      // Always check for public booking clients (backwards compatibility - bookings might have clients but no booking_type)
      let publicBookingClients: PublicBookingClient[] | undefined;
      try {
        const clients = await supabasePublicBookingClientsStorage.getPublicBookingClients(id);
        if (clients && clients.length > 0) {
          publicBookingClients = clients;
        }
      } catch (error) {
        // No clients found or error - booking is not public
      }
      
      // Determine booking type: check booking_type first, then check if it has clients, or if customer_id is null
      const hasClients = publicBookingClients && publicBookingClients.length > 0;
      const hasNoCustomer = !data.customer_id;
      const detectedBookingType = hasNoCustomer || data.booking_type === 'public' || hasClients ? 'public' : 'standard';
      
      return {
        id: data.id,
        customerId: data.customer_id,
        payerCustomerId: data.payer_customer_id || data.customer_id || null,
        payerType: data.payer_type || (data.payer_customer_id ? 'company' : null),
        locationId: data.location_id,
        contactId: data.contact_id,
        instructorId: data.instructor_id,
        courseName: data.course_name,
        courseDate: data.course_date,
        startTime: data.start_time,
        endTime: data.end_time,
        totalPrice: data.total_price,
        instructorPrice: data.instructor_price,
        instructorPO: data.instructor_po,
        clientPO: data.client_po,
        attendeeCount: data.attendee_count,
        attendees: attendees.length > 0 ? attendees : undefined,
        notes: data.notes,
        status: data.status,
        bookingType: detectedBookingType,
        publicBookingClients: publicBookingClients,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        allBookingDates: (() => {
          const mainDate = data.course_date;
          const additionalDates = data.booking_dates?.map((bd: any) => bd.date) || [];

          // Combine and deduplicate all dates - ensure we filter out any falsy values
          const allDates = mainDate ? [mainDate, ...additionalDates] : additionalDates;
          const uniqueDates = [...new Set(allDates.filter(d => d))].sort();

          // Booking dates construction - log removed to reduce console noise
          return uniqueDates;
        })(),
        allBookingDatesWithTimes: (() => {
          const mainDate = data.course_date;
          const mainStartTime = data.start_time || '09:00';
          const mainEndTime = data.end_time || '';
          
          // Create main date entry
          const mainDateEntry = mainDate ? [{
            date: mainDate,
            startTime: mainStartTime,
            endTime: mainEndTime
          }] : [];
          
          // Create additional dates entries
          const additionalDatesEntries = data.booking_dates?.map((bd: any) => ({
            date: bd.date,
            startTime: bd.start_time || '09:00',
            endTime: bd.end_time || ''
          })) || [];
          
          // Combine and deduplicate by date
          const allEntries = [...mainDateEntry, ...additionalDatesEntries];
          const uniqueEntries = allEntries.filter((entry, index, arr) => 
            arr.findIndex(e => e.date === entry.date) === index
          );
          
          return uniqueEntries.sort((a, b) => a.date.localeCompare(b.date));
        })()
      };
    } catch (error) {
      console.error('Error fetching booking by ID:', error);
      return null;
    }
  },

  async getBookingsByInstructorIdForNextWeek(instructorId: string): Promise<Booking[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      // Get next week's date range (Monday to Sunday)
      const today = new Date();
      const nextMonday = new Date(today);
      const daysUntilMonday = today.getDay() === 1 ? 7 : (8 - today.getDay()) % 7;
      nextMonday.setDate(today.getDate() + daysUntilMonday);
      nextMonday.setHours(0, 0, 0, 0);
      
      const nextSunday = new Date(nextMonday);
      nextSunday.setDate(nextMonday.getDate() + 6);
      nextSunday.setHours(23, 59, 59, 999);

      const nextMondayStr = nextMonday.toISOString().split('T')[0];
      const nextSundayStr = nextSunday.toISOString().split('T')[0];

      const bookingsSelect = `
        *,
        booking_dates(date, start_time, end_time)
      `;

      const { data: directBookings, error: directError } = await supabase
        .from('bookings')
        .select(bookingsSelect)
        .eq('instructor_id', instructorId)
        .neq('status', 'cancelled')
        .order('course_date', { ascending: true });
      
      if (directError) {
        throw directError;
      }

      // Some environments may not have booking_dates.instructor_id yet.
      // If so, keep the direct booking-level assignments instead of failing.
      let dayAssignments: any[] = [];
      const { data: dayAssignmentsData, error: dayAssignmentsError } = await supabase
        .from('booking_dates')
        .select('booking_id, date')
        .eq('instructor_id', instructorId)
        .gte('date', nextMondayStr)
        .lte('date', nextSundayStr);

      if (dayAssignmentsError) {
        const code = (dayAssignmentsError as any)?.code;
        // 42703: undefined_column (Postgres)
        if (code !== '42703') {
          throw dayAssignmentsError;
        }
      } else {
        dayAssignments = dayAssignmentsData || [];
      }

      const directIds = new Set((directBookings || []).map((b: any) => b.id));
      const dayLevelBookingIds = [
        ...new Set(dayAssignments.map((row: any) => row.booking_id).filter(Boolean))
      ].filter((id: string) => !directIds.has(id));

      let dayLevelBookings: any[] = [];
      if (dayLevelBookingIds.length > 0) {
        const { data: extraBookings, error: extraBookingsError } = await supabase
          .from('bookings')
          .select(bookingsSelect)
          .in('id', dayLevelBookingIds)
          .neq('status', 'cancelled')
          .order('course_date', { ascending: true });

        if (extraBookingsError) {
          throw extraBookingsError;
        }

        dayLevelBookings = extraBookings || [];
      }

      const data = [...(directBookings || []), ...dayLevelBookings];

      // Filter bookings that fall within next week
      const filteredBookings = (data || []).filter(booking => {
        // Check main course date
        const isMainDateInRange = booking.course_date >= nextMondayStr && booking.course_date <= nextSundayStr;

        // Check all booking dates
        let isAnyDateInRange = isMainDateInRange;
        if (booking.booking_dates && booking.booking_dates.length > 0) {
          isAnyDateInRange = booking.booking_dates.some((bd: any) => {
            return bd.date >= nextMondayStr && bd.date <= nextSundayStr;
          });
        }

        return isAnyDateInRange;
      });

      const mappedBookings: Booking[] = filteredBookings.map(booking => ({
        sourceType: 'booking',
        id: booking.id,
        customerId: booking.customer_id,
        payerCustomerId: booking.payer_customer_id || booking.customer_id || null,
        payerType: booking.payer_type || (booking.payer_customer_id ? 'company' : null),
        locationId: booking.location_id,
        contactId: booking.contact_id,
        instructorId: booking.instructor_id,
        courseName: booking.course_name,
        courseDate: booking.course_date,
        startTime: booking.start_time,
        endTime: booking.end_time,
        totalPrice: booking.total_price,
        instructorPrice: booking.instructor_price,
        instructorPO: booking.instructor_po,
        clientPO: booking.client_po,
        attendeeCount: booking.attendee_count,
        notes: booking.notes,
        status: booking.status,
        createdAt: booking.created_at,
        updatedAt: booking.updated_at,
        allBookingDates: (() => {
          const mainDate = booking.course_date;
          const additionalDates = booking.booking_dates?.map((bd: any) => bd.date) || [];
          const allDates = mainDate ? [mainDate, ...additionalDates] : additionalDates;
          return [...new Set(allDates.filter(d => d))].sort();
        })(),
        allBookingDatesWithTimes: (() => {
          const mainDate = booking.course_date;
          const mainStartTime = booking.start_time || '09:00';
          const mainEndTime = booking.end_time || '';
          
          const mainDateEntry = mainDate ? [{
            date: mainDate,
            startTime: mainStartTime,
            endTime: mainEndTime
          }] : [];
          
          const additionalDatesEntries = booking.booking_dates?.map((bd: any) => ({
            date: bd.date,
            startTime: bd.start_time || '09:00',
            endTime: bd.end_time || ''
          })) || [];
          
          const allEntries = [...mainDateEntry, ...additionalDatesEntries];
          const uniqueEntries = allEntries.filter((entry, index, arr) => 
            arr.findIndex(e => e.date === entry.date) === index
          );
          
          return uniqueEntries.sort((a, b) => a.date.localeCompare(b.date));
        })()
      }));

      // Also include instructor assignments created as course_sessions (Session workspace),
      // which are separate from legacy bookings.
      let mappedSessions: Booking[] = [];
      const { data: sessionsData, error: sessionsError } = await supabase
        .from('course_sessions')
        .select('id, course_name, location_id, instructor_id, date_blocks, notes, interpreter_briefing, arrival_instructions, parking_access, site_hazards_ppe, on_site_contact_override_name, on_site_contact_override_phone, on_site_contact_override_email, interpreter_reminders, interpreter_briefing_updated_at, interpreter_briefing_updated_by, created_at, updated_at')
        .eq('instructor_id', instructorId);

      if (!sessionsError && sessionsData && sessionsData.length > 0) {
        const sessionIds = sessionsData.map((s: any) => s.id);
        let attendeesBySession = new Map<string, number>();
        const { data: attendeesData, error: attendeesError } = await supabase
          .from('course_session_attendees')
          .select('booking_id, status')
          .in('booking_id', sessionIds);

        if (!attendeesError && attendeesData) {
          attendeesData.forEach((row: any) => {
            const status = row.status || 'booked';
            if (status !== 'cancelled') {
              attendeesBySession.set(row.booking_id, (attendeesBySession.get(row.booking_id) || 0) + 1);
            }
          });
        }

        mappedSessions = (sessionsData || [])
          .map((session: any) => {
            const dateBlocks = Array.isArray(session.date_blocks) ? session.date_blocks : [];
            const dates = dateBlocks
              .map((d: any) => d?.date)
              .filter((d: any) => typeof d === 'string' && d.length > 0)
              .sort();
            if (dates.length === 0) return null;

            const inNextWeek = dates.some((d: string) => d >= nextMondayStr && d <= nextSundayStr);
            if (!inNextWeek) return null;

            const firstDate = dates[0];
            return {
              sourceType: 'course_session',
              id: session.id,
              locationId: session.location_id || undefined,
              instructorId: session.instructor_id || undefined,
              courseName: session.course_name || 'Course Session',
              courseDate: firstDate,
              startTime: undefined,
              endTime: undefined,
              attendeeCount: attendeesBySession.get(session.id) || 0,
              notes: session.notes || undefined,
              interpreterBriefing: session.interpreter_briefing || undefined,
              arrivalInstructions: session.arrival_instructions || undefined,
              parkingAccess: session.parking_access || undefined,
              siteHazardsPpe: session.site_hazards_ppe || undefined,
              onSiteContactOverrideName: session.on_site_contact_override_name || undefined,
              onSiteContactOverridePhone: session.on_site_contact_override_phone || undefined,
              onSiteContactOverrideEmail: session.on_site_contact_override_email || undefined,
              interpreterReminders: session.interpreter_reminders || undefined,
              interpreterBriefingUpdatedAt: session.interpreter_briefing_updated_at || undefined,
              interpreterBriefingUpdatedBy: session.interpreter_briefing_updated_by || undefined,
              status: 'scheduled',
              createdAt: session.created_at,
              updatedAt: session.updated_at,
              allBookingDates: dates,
              allBookingDatesWithTimes: dateBlocks
                .filter((d: any) => d?.date)
                .map((d: any) => ({
                  date: d.date,
                  startTime: d.startTime || d.start_time || '09:00',
                  endTime: d.endTime || d.end_time || ''
                }))
                .sort((a: any, b: any) => a.date.localeCompare(b.date))
            } satisfies Booking;
          })
          .filter((b: Booking | null): b is Booking => b !== null);
      }

      return [...mappedBookings, ...mappedSessions].sort((a, b) => {
        const da = (a.allBookingDates?.[0] || a.courseDate || '').toString();
        const db = (b.allBookingDates?.[0] || b.courseDate || '').toString();
        return da.localeCompare(db);
      });
    } catch (error) {
      console.error('Error fetching bookings by instructor ID for next week:', error);
      return [];
    }
  },

  async getBookingsByInstructorId(instructorId: string): Promise<Booking[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      const bookingsSelect = `
        *,
        booking_dates(date, start_time, end_time)
      `;

      const { data: directBookings, error: directError } = await supabase
        .from('bookings')
        .select(bookingsSelect)
        .eq('instructor_id', instructorId)
        .order('course_date', { ascending: false });
      
      if (directError) {
        throw directError;
      }

      // Some environments may not have booking_dates.instructor_id yet.
      // If so, keep the direct booking-level assignments instead of failing.
      let dayAssignments: any[] = [];
      const { data: dayAssignmentsData, error: dayAssignmentsError } = await supabase
        .from('booking_dates')
        .select('booking_id')
        .eq('instructor_id', instructorId);

      if (dayAssignmentsError) {
        const code = (dayAssignmentsError as any)?.code;
        // 42703: undefined_column (Postgres)
        if (code !== '42703') {
          throw dayAssignmentsError;
        }
      } else {
        dayAssignments = dayAssignmentsData || [];
      }

      const directIds = new Set((directBookings || []).map((b: any) => b.id));
      const dayLevelBookingIds = [
        ...new Set(dayAssignments.map((row: any) => row.booking_id).filter(Boolean))
      ].filter((id: string) => !directIds.has(id));

      let dayLevelBookings: any[] = [];
      if (dayLevelBookingIds.length > 0) {
        const { data: extraBookings, error: extraBookingsError } = await supabase
          .from('bookings')
          .select(bookingsSelect)
          .in('id', dayLevelBookingIds)
          .order('course_date', { ascending: false });

        if (extraBookingsError) {
          throw extraBookingsError;
        }

        dayLevelBookings = extraBookings || [];
      }

      const data = [...(directBookings || []), ...dayLevelBookings];

      const mappedBookings: Booking[] = data.map(booking => ({
        sourceType: 'booking',
        id: booking.id,
        customerId: booking.customer_id,
        payerCustomerId: booking.payer_customer_id || booking.customer_id || null,
        payerType: booking.payer_type || (booking.payer_customer_id ? 'company' : null),
        locationId: booking.location_id,
        contactId: booking.contact_id,
        instructorId: booking.instructor_id,
        courseName: booking.course_name,
        courseDate: booking.course_date,
        startTime: booking.start_time,
        endTime: booking.end_time,
        totalPrice: booking.total_price,
        instructorPrice: booking.instructor_price,
        instructorPO: booking.instructor_po,
        clientPO: booking.client_po,
        attendeeCount: booking.attendee_count,
        notes: booking.notes,
        status: booking.status,
        createdAt: booking.created_at,
        updatedAt: booking.updated_at,
        allBookingDates: (() => {
          const mainDate = booking.course_date;
          const additionalDates = booking.booking_dates?.map((bd: any) => bd.date) || [];
          const allDates = mainDate ? [mainDate, ...additionalDates] : additionalDates;
          return [...new Set(allDates.filter(d => d))].sort();
        })(),
        allBookingDatesWithTimes: (() => {
          const mainDate = booking.course_date;
          const mainStartTime = booking.start_time || '09:00';
          const mainEndTime = booking.end_time || '';
          
          const mainDateEntry = mainDate ? [{
            date: mainDate,
            startTime: mainStartTime,
            endTime: mainEndTime
          }] : [];
          
          const additionalDatesEntries = booking.booking_dates?.map((bd: any) => ({
            date: bd.date,
            startTime: bd.start_time || '09:00',
            endTime: bd.end_time || ''
          })) || [];
          
          const allEntries = [...mainDateEntry, ...additionalDatesEntries];
          const uniqueEntries = allEntries.filter((entry, index, arr) => 
            arr.findIndex(e => e.date === entry.date) === index
          );
          
          return uniqueEntries.sort((a, b) => a.date.localeCompare(b.date));
        })()
      }));

      // Also include instructor assignments created as course_sessions (Session workspace),
      // which are separate from legacy bookings.
      let mappedSessions: Booking[] = [];
      const { data: sessionsData, error: sessionsError } = await supabase
        .from('course_sessions')
        .select('id, course_name, location_id, instructor_id, date_blocks, notes, interpreter_briefing, arrival_instructions, parking_access, site_hazards_ppe, on_site_contact_override_name, on_site_contact_override_phone, on_site_contact_override_email, interpreter_reminders, interpreter_briefing_updated_at, interpreter_briefing_updated_by, created_at, updated_at')
        .eq('instructor_id', instructorId);

      if (!sessionsError && sessionsData && sessionsData.length > 0) {
        const sessionIds = sessionsData.map((s: any) => s.id);
        let attendeesBySession = new Map<string, number>();
        const { data: attendeesData, error: attendeesError } = await supabase
          .from('course_session_attendees')
          .select('booking_id, status')
          .in('booking_id', sessionIds);

        if (!attendeesError && attendeesData) {
          attendeesData.forEach((row: any) => {
            const status = row.status || 'booked';
            if (status !== 'cancelled') {
              attendeesBySession.set(row.booking_id, (attendeesBySession.get(row.booking_id) || 0) + 1);
            }
          });
        }

        mappedSessions = (sessionsData || [])
          .map((session: any) => {
            const dateBlocks = Array.isArray(session.date_blocks) ? session.date_blocks : [];
            const dates = dateBlocks
              .map((d: any) => d?.date)
              .filter((d: any) => typeof d === 'string' && d.length > 0)
              .sort();
            if (dates.length === 0) return null;

            const firstDate = dates[0];
            return {
              sourceType: 'course_session',
              id: session.id,
              locationId: session.location_id || undefined,
              instructorId: session.instructor_id || undefined,
              courseName: session.course_name || 'Course Session',
              courseDate: firstDate,
              startTime: undefined,
              endTime: undefined,
              attendeeCount: attendeesBySession.get(session.id) || 0,
              notes: session.notes || undefined,
              interpreterBriefing: session.interpreter_briefing || undefined,
              arrivalInstructions: session.arrival_instructions || undefined,
              parkingAccess: session.parking_access || undefined,
              siteHazardsPpe: session.site_hazards_ppe || undefined,
              onSiteContactOverrideName: session.on_site_contact_override_name || undefined,
              onSiteContactOverridePhone: session.on_site_contact_override_phone || undefined,
              onSiteContactOverrideEmail: session.on_site_contact_override_email || undefined,
              interpreterReminders: session.interpreter_reminders || undefined,
              interpreterBriefingUpdatedAt: session.interpreter_briefing_updated_at || undefined,
              interpreterBriefingUpdatedBy: session.interpreter_briefing_updated_by || undefined,
              status: 'scheduled',
              createdAt: session.created_at,
              updatedAt: session.updated_at,
              allBookingDates: dates,
              allBookingDatesWithTimes: dateBlocks
                .filter((d: any) => d?.date)
                .map((d: any) => ({
                  date: d.date,
                  startTime: d.startTime || d.start_time || '09:00',
                  endTime: d.endTime || d.end_time || ''
                }))
                .sort((a: any, b: any) => a.date.localeCompare(b.date))
            } satisfies Booking;
          })
          .filter((b: Booking | null): b is Booking => b !== null);
      }

      return [...mappedBookings, ...mappedSessions].sort((a, b) => {
        const da = (a.allBookingDates?.[0] || a.courseDate || '').toString();
        const db = (b.allBookingDates?.[0] || b.courseDate || '').toString();
        return db.localeCompare(da);
      });
    } catch (error) {
      console.error('Error fetching bookings by instructor ID:', error);
      return [];
    }
  },

  async addBooking(booking: Booking): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    if (await deferBookingWriteToOfflineIfNeeded('create', booking as unknown as CrmBooking)) {
      return;
    }

    try {
      // Generate automatic instructor PO if not provided
      let instructorPO = booking.instructorPO;
      if (!instructorPO || instructorPO.trim() === '') {
        try {
          // Get all existing PO numbers to find the highest numeric one
          const { data: existingPOs, error: poError } = await supabase
            .from('bookings')
            .select('instructor_po')
            .not('instructor_po', 'is', null)
            .like('instructor_po', 'RC%');
          
          if (poError) {
            console.error('Error fetching existing PO numbers:', poError);
            instructorPO = 'RC1000'; // Fallback to starting number
          } else if (existingPOs && existingPOs.length > 0) {
            // Find the highest numeric PO number (ignore manually entered ones)
            let highestNumber = 999; // Start below RC1000
            
            existingPOs.forEach(po => {
              const match = po.instructor_po.match(/RC(\d+)/);
              if (match) {
                const number = parseInt(match[1]);
                // Only consider numbers >= 1000 (proper sequence)
                if (number >= 1000 && number > highestNumber) {
                  highestNumber = number;
                }
              }
            });
            
            // Generate next number in sequence
            const nextNumber = highestNumber + 1;
            instructorPO = `RC${nextNumber}`;
          } else {
            instructorPO = 'RC1000'; // First PO number
          }
        } catch (error) {
          console.error('Error generating instructor PO:', error);
          instructorPO = 'RC1000'; // Fallback
        }
      }

      const bookingInsertRow: Record<string, unknown> = {
        id: booking.id,
        customer_id: (booking.customerId && typeof booking.customerId === 'string' && booking.customerId.trim() !== '') ? booking.customerId : null,
        payer_customer_id: (booking.payerCustomerId && typeof booking.payerCustomerId === 'string' && booking.payerCustomerId.trim() !== '') ? booking.payerCustomerId : null,
        payer_type: booking.payerType || null,
        location_id: (booking.locationId && typeof booking.locationId === 'string' && booking.locationId.trim() !== '') ? booking.locationId : null,
        contact_id: (booking.contactId && typeof booking.contactId === 'string' && booking.contactId.trim() !== '') ? booking.contactId : null,
        instructor_id: (booking.instructorId && typeof booking.instructorId === 'string' && booking.instructorId.trim() !== '') ? booking.instructorId : null,
        course_name: booking.courseName,
        course_date: booking.courseDate,
        start_time: booking.startTime,
        end_time: booking.endTime,
        total_price: booking.totalPrice,
        instructor_price: booking.instructorPrice,
        instructor_po: instructorPO,
        client_po: booking.clientPO,
        attendee_count: booking.attendeeCount,
        notes: booking.notes,
        status: booking.status,
        booking_type: booking.bookingType || 'standard',
      };
      if ((booking as any).source) {
        bookingInsertRow.source = (booking as any).source;
      }
      let { error } = await supabase.from('bookings').insert(bookingInsertRow);
      if (error && /could not find the 'source' column|column bookings\.source does not exist/i.test(String(error?.message || ''))) {
        const { source: _omitSource, ...withoutSource } = bookingInsertRow as Record<string, unknown> & { source?: unknown };
        ({ error } = await supabase.from('bookings').insert(withoutSource));
      }

      if (error) {
        throw error;
      }
      
      // Clear local cache after adding booking to ensure fresh data is loaded next time
      try {
        const { localBookingStorage } = await import('./localStorageCache');
        localBookingStorage.clear();
        console.log('🗑️ Cleared booking cache after adding new booking');
      } catch (cacheError) {
        console.warn('Failed to clear booking cache:', cacheError);
      }

      // Add booking dates if provided
      if (booking.allBookingDates && booking.allBookingDates.length > 1) {
        // Only store additional dates, not the main course date
        const additionalDatesOnly = booking.allBookingDates.filter(date => date !== booking.courseDate);
        if (additionalDatesOnly.length > 0) {
          await supabaseBookingDatesStorage.addBookingDates(booking.id, additionalDatesOnly);
        }
      }

      // Mark instructor as unavailable for the booking dates
      if (booking.instructorId) {
        try {
          const { markInstructorUnavailableForBooking } = await import('./instructorAvailabilityService');
          
          // Mark unavailable for main course date
          await markInstructorUnavailableForBooking(
            booking.instructorId,
            booking.courseDate,
            booking.startTime,
            booking.endTime,
            booking.id,
            booking.courseName
          );

          // Mark unavailable for all dates in allBookingDates
          // Process all dates to ensure they're all marked, even if courseDate is in the list
          if (booking.allBookingDates && booking.allBookingDates.length > 0) {
            for (const date of booking.allBookingDates) {
              // Skip the main courseDate as it's already processed above
              if (date !== booking.courseDate) {
                // For additional dates, use the main booking times or default
                await markInstructorUnavailableForBooking(
                  booking.instructorId,
                  date,
                  booking.startTime || '09:00',
                  booking.endTime,
                  booking.id,
                  booking.courseName
                );
              }
            }
          }
        } catch (availabilityError) {
          console.warn('Failed to mark instructor unavailable (non-critical):', availabilityError);
          // Don't throw - availability marking shouldn't break booking creation
        }
      }

      try {
        const [customerRes, contactRes] = await Promise.all([
          booking.customerId
            ? supabase
                .from('customers')
                .select('id, company_name, preferred_language, email_address')
                .eq('id', booking.customerId)
                .maybeSingle()
            : Promise.resolve({ data: null }),
          booking.contactId
            ? supabase
                .from('contacts')
                .select('id, first_name, last_name, email')
                .eq('id', booking.contactId)
                .maybeSingle()
            : Promise.resolve({ data: null }),
        ]);
        const c = customerRes.data as any;
        const ct = contactRes.data as any;
        const recipientEmail = ct?.email || c?.email_address;
        if (recipientEmail) {
          await EmailAutomationOrchestrator.processEvent({
            trigger: 'booking_created',
            entityId: booking.id,
            subjectContext: booking.courseName,
            relatedBookingId: booking.id,
            placeholders: {
              '{{CourseName}}': booking.courseName || '',
              '{{CourseDate}}': booking.courseDate || '',
            },
            recipients: [
              {
                email: recipientEmail,
                name: `${ct?.first_name || ''} ${ct?.last_name || ''}`.trim() || c?.company_name || 'Customer',
                customerId: c?.id,
                preferredLanguage: c?.preferred_language || undefined,
              },
            ],
          });
        }
      } catch (automationErr) {
        console.warn('Booking created automation failed (non-blocking):', automationErr);
      }
    } catch (error) {
      console.error('Error adding booking:', error);
      throw error;
    }
  },

  async updateBooking(booking: Booking): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    if (await deferBookingWriteToOfflineIfNeeded('update', booking as unknown as CrmBooking)) {
      return;
    }

    try {
      // Get old booking data to compare instructor and dates
      let oldBooking: Booking | null = null;
      try {
        oldBooking = await this.getBookingById(booking.id);
      } catch (error) {
        console.warn('Could not fetch old booking data for availability update:', error);
      }
      // Generate automatic instructor PO if not provided
      let instructorPO = booking.instructorPO;
      if (!instructorPO || instructorPO.trim() === '') {
        try {
          // Get all existing PO numbers to find the highest numeric one
          const { data: existingPOs, error: poError } = await supabase
            .from('bookings')
            .select('instructor_po')
            .not('instructor_po', 'is', null)
            .like('instructor_po', 'RC%');
          
          if (poError) {
            console.error('Error fetching existing PO numbers:', poError);
            instructorPO = 'RC1000'; // Fallback to starting number
          } else if (existingPOs && existingPOs.length > 0) {
            // Find the highest numeric PO number (ignore manually entered ones)
            let highestNumber = 999; // Start below RC1000
            
            existingPOs.forEach(po => {
              const match = po.instructor_po.match(/RC(\d+)/);
              if (match) {
                const number = parseInt(match[1]);
                // Only consider numbers >= 1000 (proper sequence)
                if (number >= 1000 && number > highestNumber) {
                  highestNumber = number;
                }
              }
            });
            
            // Generate next number in sequence
            const nextNumber = highestNumber + 1;
            instructorPO = `RC${nextNumber}`;
          } else {
            instructorPO = 'RC1000'; // First PO number
          }
        } catch (error) {
          console.error('Error generating instructor PO:', error);
          instructorPO = 'RC1000'; // Fallback
        }
      }

      const { error } = await supabase
        .from('bookings')
        .update({
          customer_id: (booking.customerId && typeof booking.customerId === 'string' && booking.customerId.trim() !== '') ? booking.customerId : null,
          payer_customer_id: (booking.payerCustomerId && typeof booking.payerCustomerId === 'string' && booking.payerCustomerId.trim() !== '') ? booking.payerCustomerId : null,
          payer_type: booking.payerType || null,
          location_id: (booking.locationId && typeof booking.locationId === 'string' && booking.locationId.trim() !== '') ? booking.locationId : null,
          contact_id: (booking.contactId && typeof booking.contactId === 'string' && booking.contactId.trim() !== '') ? booking.contactId : null,
          instructor_id: (booking.instructorId && typeof booking.instructorId === 'string' && booking.instructorId.trim() !== '') ? booking.instructorId : null,
          course_name: booking.courseName,
          course_date: booking.courseDate,
          start_time: booking.startTime ?? null,
          end_time: booking.endTime ?? null,
          total_price: booking.totalPrice ?? null,
          instructor_price: booking.instructorPrice ?? null,
          instructor_po: instructorPO,
          client_po: booking.clientPO ?? null,
          attendee_count: booking.attendeeCount,
          notes: booking.notes ?? null,
          status: booking.status,
          booking_type: booking.bookingType || 'standard',
          updated_at: new Date().toISOString()
        })
        .eq('id', booking.id);
      
      if (error) {
        throw error;
      }
      
      // Clear local cache after update to ensure fresh data is loaded next time
      try {
        const { localBookingStorage } = await import('./localStorageCache');
        localBookingStorage.clear();
        console.log('🗑️ Cleared booking cache after update to force reload from database');
      } catch (cacheError) {
        console.warn('Failed to clear booking cache:', cacheError);
      }

      // Update booking dates
      if (booking.allBookingDates && booking.allBookingDates.length > 0) {
        // Clear existing additional dates
        await supabaseBookingDatesStorage.deleteBookingDates(booking.id);
        // Only store additional dates, not the main course date
        const additionalDatesOnly = booking.allBookingDates.filter(date => date !== booking.courseDate);
        if (additionalDatesOnly.length > 0) {
          await supabaseBookingDatesStorage.addBookingDates(booking.id, additionalDatesOnly);
        }
      }

      // Sync instructor availability when dates or instructor change
      try {
        const { markInstructorUnavailableForBooking, removeInstructorUnavailabilityForBooking } = await import('./instructorAvailabilityService');
        const oldDates = (oldBooking?.allBookingDates && oldBooking.allBookingDates.length > 0)
          ? oldBooking.allBookingDates
          : (oldBooking ? [oldBooking.courseDate] : []);
        const newDates = (booking.allBookingDates && booking.allBookingDates.length > 0)
          ? booking.allBookingDates
          : [booking.courseDate];
        const oldInstructorId = oldBooking?.instructorId;
        const newInstructorId = booking.instructorId;

        if (oldInstructorId !== newInstructorId) {
          // Instructor changed: remove all old dates from old instructor, add all new dates to new instructor
          for (const date of oldDates.filter(Boolean) as string[]) {
            await removeInstructorUnavailabilityForBooking(oldInstructorId, date, undefined, undefined, booking.id);
          }
          if (newInstructorId) {
            for (const date of newDates.filter(Boolean) as string[]) {
              await markInstructorUnavailableForBooking(
                newInstructorId,
                date,
                undefined,
                undefined,
                booking.id,
                booking.courseName
              );
            }
          }
        } else if (newInstructorId) {
          // Same instructor: remove dates no longer in booking, add new dates
          const datesToRemove = oldDates.filter(d => d && !newDates.includes(d)) as string[];
          const datesToAdd = newDates.filter(d => d && !oldDates.includes(d)) as string[];
          for (const date of datesToRemove) {
            await removeInstructorUnavailabilityForBooking(newInstructorId, date, undefined, undefined, booking.id);
          }
          for (const date of datesToAdd) {
            await markInstructorUnavailableForBooking(
              newInstructorId,
              date,
              undefined,
              undefined,
              booking.id,
              booking.courseName
            );
          }
        }
      } catch (availabilityError) {
        console.warn('Failed to sync instructor availability (non-critical):', availabilityError);
      }

      try {
        const [customerRes, contactRes] = await Promise.all([
          booking.customerId
            ? supabase
                .from('customers')
                .select('id, company_name, preferred_language, email_address')
                .eq('id', booking.customerId)
                .maybeSingle()
            : Promise.resolve({ data: null }),
          booking.contactId
            ? supabase
                .from('contacts')
                .select('id, first_name, last_name, email')
                .eq('id', booking.contactId)
                .maybeSingle()
            : Promise.resolve({ data: null }),
        ]);
        const c = customerRes.data as any;
        const ct = contactRes.data as any;
        const recipientEmail = ct?.email || c?.email_address;
        if (recipientEmail) {
          await EmailAutomationOrchestrator.processEvent({
            trigger: 'booking_updated',
            entityId: booking.id,
            subjectContext: booking.courseName,
            relatedBookingId: booking.id,
            placeholders: {
              '{{CourseName}}': booking.courseName || '',
              '{{CourseDate}}': booking.courseDate || '',
            },
            recipients: [
              {
                email: recipientEmail,
                name: `${ct?.first_name || ''} ${ct?.last_name || ''}`.trim() || c?.company_name || 'Customer',
                customerId: c?.id,
                preferredLanguage: c?.preferred_language || undefined,
              },
            ],
          });
        }
      } catch (automationErr) {
        console.warn('Booking updated automation failed (non-blocking):', automationErr);
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      throw error;
    }
  },

  async deleteBooking(id: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    if (await deferBookingWriteToOfflineIfNeeded('delete', { id } as CrmBooking)) {
      return;
    }

    try {
      // Get booking data before deleting to remove availability
      let booking: Booking | null = null;
      try {
        booking = await this.getBookingById(id);
      } catch (error) {
        console.warn('Could not fetch booking data for availability cleanup:', error);
      }

      // Delete booking dates first (handled by CASCADE in database)
      const { error } = await supabase
        .from('bookings')
        .delete()
        .eq('id', id);
      
      if (error) {
        throw error;
      }

      // Remove instructor unavailability
      if (booking && booking.instructorId) {
        try {
          const { removeInstructorUnavailabilityForBooking } = await import('./instructorAvailabilityService');
          
          // Remove from main date
          await removeInstructorUnavailabilityForBooking(
            booking.instructorId,
            booking.courseDate,
            booking.startTime,
            booking.endTime,
            id
          );
          
          // Remove from additional dates
          if (booking.allBookingDates && booking.allBookingDates.length > 1) {
            for (const date of booking.allBookingDates) {
              if (date !== booking.courseDate) {
                await removeInstructorUnavailabilityForBooking(
                  booking.instructorId,
                  date,
                  booking.startTime || '09:00',
                  booking.endTime,
                  id
                );
              }
            }
          }
        } catch (availabilityError) {
          console.warn('Failed to remove instructor availability (non-critical):', availabilityError);
          // Don't throw - availability removal shouldn't break booking deletion
        }
      }
    } catch (error) {
      console.error('Error deleting booking:', error);
      throw error;
    }
  },

  async getBookingsOld(): Promise<Booking[]> {
    const { data, error } = await supabase
      .from('bookings')
      .select(`
        *,
        booking_dates(date)
      `)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return (data || []).map(booking => ({
      ...booking,
      allBookingDates: booking.booking_dates?.map((bd: any) => bd.date) || []
    }));
  },

  async addBookingOld(booking: Booking): Promise<void> {
    const { data, error } = await supabase
      .from('bookings')
      .insert([{
        id: booking.id,
        customer_name: booking.customerName,
        contact_person: booking.contactPerson,
        contact_email: booking.contactEmail,
        contact_phone: booking.contactPhone,
        course_type: booking.courseType,
        number_of_attendees: booking.numberOfAttendees,
        location: booking.location,
        date: booking.date,
        notes: booking.notes,
        status: booking.status,
        created_at: booking.createdAt || new Date().toISOString(),
        updated_at: booking.updatedAt || new Date().toISOString()
      }])
      .select()
      .single();
    
    if (error) throw error;

    // Add booking dates if provided
    if (booking.allBookingDates && booking.allBookingDates.length > 0) {
      await this.addBookingDates(booking.id, booking.allBookingDates);
    }
  },

  async updateBookingOld(booking: Booking): Promise<void> {
    const { error } = await supabase
      .from('bookings')
      .update({
        customer_name: booking.customerName,
        contact_person: booking.contactPerson,
        contact_email: booking.contactEmail,
        contact_phone: booking.contactPhone,
        course_type: booking.courseType,
        number_of_attendees: booking.numberOfAttendees,
        location: booking.location,
        date: booking.date,
        notes: booking.notes,
        status: booking.status,
        updated_at: new Date().toISOString()
      })
      .eq('id', booking.id);
    
    if (error) throw error;

    // Update booking dates
    if (booking.allBookingDates) {
      await this.deleteBookingDates(booking.id);
      await this.addBookingDates(booking.id, booking.allBookingDates);
    }
  },

  async deleteBookingOld(id: string): Promise<void> {
    // Delete booking dates first
    await this.deleteBookingDates(id);
    
    const { error } = await supabase
      .from('bookings')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  async addBookingDates(bookingId: string, dates: string[]): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    if (dates.length === 0) return;

    try {
      const bookingDates = dates.map(date => ({
        booking_id: bookingId,
        date: date
      }));

      const { error } = await supabase
        .from('booking_dates')
        .insert(bookingDates);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error adding booking dates:', error);
      throw error;
    }
  },

  async deleteBookingDates(bookingId: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      const { error } = await supabase
        .from('booking_dates')
        .delete()
        .eq('booking_id', bookingId);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error deleting booking dates:', error);
      throw error;
    }
  },

  async updateBookingDates(bookingId: string, dates: string[]): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      console.log(`📅 BOOKING DATES: Updating dates for booking ${bookingId} with ${dates.length} dates:`, dates);
      
      // First delete existing booking dates
      await this.deleteBookingDates(bookingId);
      
      // Then add new booking dates if any
      if (dates.length > 0) {
        await this.addBookingDates(bookingId, dates);
      }
      
      console.log(`📅 BOOKING DATES: Successfully updated booking dates`);
    } catch (error) {
      console.error('Error updating booking dates:', error);
      throw error;
    }
  },

  async getCalendarEvents(): Promise<any[]> {
    const { data, error } = await supabase
      .rpc('get_booking_calendar_events');
    
    if (error) throw error;
    return data || [];
  }
};

// Instructor Storage
export const supabaseInstructorStorage = {
  async getInstructors(limit?: number, offset?: number): Promise<Instructor[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      let query = supabase
        .from('instructors')
        .select('*')
        .order('first_name');
      
      // Apply pagination if provided
      if (limit !== undefined) {
        query = query.limit(limit);
      }
      if (offset !== undefined) {
        query = query.range(offset, offset + (limit || 1000) - 1);
      }
      
      const { data, error } = await query;
      
      if (error) {
        throw error;
      }

      const instructors = (data || []).map(instructor => ({
        id: instructor.id,
        firstName: instructor.first_name,
        lastName: instructor.last_name,
        email: instructor.email,
        phone: instructor.phone || null,
        createdAt: instructor.created_at,
        updatedAt: instructor.updated_at
      }));

      // Note: Instructors are typically cached via instructorCache, not localInstructorStorage
      // For now, we'll just return the data without full dataset caching
      return instructors;
    } catch (error: any) {
      console.error('Error fetching instructors:', error);
      
      // Check if this is a network/service error (not auth error)
      const isNetworkError = error?.message?.includes('Failed to fetch') || 
                            error?.message?.includes('NetworkError') ||
                            error?.code === 'ECONNREFUSED' ||
                            error?.code === 'ETIMEDOUT';
      
      // Try to use instructorCache if available (for individual lookups)
      if (isNetworkError) {
        const { instructorCache } = await import('./localStorageCache');
        // Note: instructorCache is for individual lookups, not full lists
        // For now, return empty array - instructors are typically small datasets
        console.log('⚠️ Network error fetching instructors - returning empty array');
      }
      
      return [];
    }
  },

  // Server-side instructor search (lightweight fields + phone for global search)
  async searchInstructors(
    term: string,
    limit: number = 10
  ): Promise<Array<Pick<Instructor, 'id' | 'firstName' | 'lastName' | 'email' | 'phone'>>> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    const trimmed = term.trim();
    if (!trimmed) {
      return [];
    }

    const safe = trimmed.replace(/,/g, ' ');

    try {
      let query = supabase
        .from('instructors')
        .select('id, first_name, last_name, email, phone')
        .order('first_name')
        .limit(limit);

      const orBase = `first_name.ilike.%${safe}%,last_name.ilike.%${safe}%,email.ilike.%${safe}%`;
      let { data, error } = await query.or(`${orBase},phone.ilike.%${safe}%`);

      if (error && String((error as any)?.message || '').toLowerCase().includes('phone')) {
        const retry = await supabase
          .from('instructors')
          .select('id, first_name, last_name, email')
          .or(orBase)
          .order('first_name')
          .limit(limit);
        data = retry.data;
        error = retry.error;
      }

      if (error) {
        throw error;
      }

      return (data || []).map((instructor: any) => ({
        id: instructor.id,
        firstName: instructor.first_name,
        lastName: instructor.last_name,
        email: instructor.email,
        phone: instructor.phone ?? null
      }));
    } catch (error) {
      console.error('Error searching instructors:', error);
      return [];
    }
  },

  async getInstructorByEmail(email: string): Promise<Instructor | null> {
    if (!isSupabaseConfigured()) {
      return null;
    }

    try {
      const normalizedEmail = email.trim().toLowerCase();

      const { data, error } = await supabase
        .from('instructors')
        .select('*')
        .eq('email', normalizedEmail)
        .maybeSingle();
      
      if (error) {
        throw error;
      }

      let instructorData = data;

      // Fallback for legacy rows with mixed-case or whitespace-padded emails.
      if (!instructorData) {
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('instructors')
          .select('*');

        if (fallbackError) {
          throw fallbackError;
        }

        instructorData = (fallbackData || []).find((row: any) => {
          const rowEmail = typeof row.email === 'string' ? row.email.trim().toLowerCase() : '';
          return rowEmail === normalizedEmail;
        }) || null;
      }

      if (!instructorData) {
        return null;
      }

      return {
        id: instructorData.id,
        firstName: instructorData.first_name,
        lastName: instructorData.last_name,
        email: instructorData.email,
        phone: instructorData.phone || null,
        createdAt: instructorData.created_at,
        updatedAt: instructorData.updated_at
      };
    } catch (error) {
      console.error('Error fetching instructor by email:', error);
      return null;
    }
  },

  async addInstructor(instructor: Omit<Instructor, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      const rowWithPhone = {
        first_name: instructor.firstName,
        last_name: instructor.lastName,
        email: instructor.email,
        phone: instructor.phone || null
      };
      let { error } = await supabase
        .from('instructors')
        .insert(rowWithPhone);
      if (error && String((error as any)?.message || '').toLowerCase().includes('phone')) {
        const retry = await supabase
          .from('instructors')
          .insert({
            first_name: instructor.firstName,
            last_name: instructor.lastName,
            email: instructor.email
          });
        error = retry.error;
      }
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error adding instructor:', error);
      throw error;
    }
  },

  async updateInstructor(instructor: Instructor): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      const rowWithPhone = {
        first_name: instructor.firstName,
        last_name: instructor.lastName,
        email: instructor.email,
        phone: instructor.phone || null,
        updated_at: new Date().toISOString()
      };
      let { error } = await supabase
        .from('instructors')
        .update(rowWithPhone)
        .eq('id', instructor.id);
      if (error && String((error as any)?.message || '').toLowerCase().includes('phone')) {
        const retry = await supabase
          .from('instructors')
          .update({
            first_name: instructor.firstName,
            last_name: instructor.lastName,
            email: instructor.email,
            updated_at: new Date().toISOString()
          })
          .eq('id', instructor.id);
        error = retry.error;
      }
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error updating instructor:', error);
      throw error;
    }
  },

  async deleteInstructor(id: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      const { error } = await supabase
        .from('instructors')
        .delete()
        .eq('id', id);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error deleting instructor:', error);
      throw error;
    }
  },

  async getInstructorsOld(): Promise<Instructor[]> {
    const { data, error } = await supabase
      .from('instructors')
      .select('*')
      .order('name');
    
    if (error) throw error;
    return data || [];
  },

  async addInstructorOld(instructor: Instructor): Promise<void> {
    const { error } = await supabase
      .from('instructors')
      .insert([instructor]);
    
    if (error) throw error;
  },

  async updateInstructorOld(instructor: Instructor): Promise<void> {
    const { error } = await supabase
      .from('instructors')
      .update(instructor)
      .eq('id', instructor.id);
    
    if (error) throw error;
  },

  async deleteInstructorOld(id: string): Promise<void> {
    const { error } = await supabase
      .from('instructors')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// Booking Dates Storage
export const supabaseBookingDatesStorage = {
  async getAllBookingDates(): Promise<Array<{id: string; bookingId: string; date: string; createdAt: string; updatedAt: string}>> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('booking_dates')
        .select('*')
        .order('date');
      
      if (error) {
        throw error;
      }

      return (data || []).map(item => ({
        id: item.id,
        bookingId: item.booking_id,
        date: item.date,
        createdAt: item.created_at,
        updatedAt: item.updated_at
      }));
    } catch (error) {
      console.error('Error fetching all booking dates:', error);
      return [];
    }
  },

  async getBookingDates(bookingId: string): Promise<string[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      console.log(`📅 BOOKING DATES: Fetching dates for booking ${bookingId}`);
      const { data, error } = await supabase
        .from('booking_dates')
        .select('date')
        .eq('booking_id', bookingId)
        .order('date');
      
      if (error) {
        console.error('Error fetching booking dates:', error);
        return [];
      }

      const dates = (data || []).map(item => item.date);
      console.log(`📅 BOOKING DATES: Found ${dates.length} dates:`, dates);
      return dates;
    } catch (error) {
      console.error('Error fetching booking dates:', error);
      return [];
    }
  },

  async getBookingDatesWithTimes(bookingId: string): Promise<Array<{date: string, startTime: string, endTime: string}>> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      console.log(`📅 BOOKING DATES WITH TIMES: Fetching dates and times for booking ${bookingId}`);
      const { data, error } = await supabase
        .from('booking_dates')
        .select('date, start_time, end_time')
        .eq('booking_id', bookingId)
        .order('date');
      
      if (error) {
        console.error('Error fetching booking dates with times:', error);
        return [];
      }

      const datesWithTimes = (data || []).map(item => ({
        date: item.date,
        startTime: item.start_time || '09:00',
        endTime: item.end_time || ''
      }));
      console.log(`📅 BOOKING DATES WITH TIMES: Found ${datesWithTimes.length} dates:`, datesWithTimes);
      return datesWithTimes;
    } catch (error) {
      console.error('Error fetching booking dates with times:', error);
      return [];
    }
  },

  async getCalendarEvents(startDate?: string, endDate?: string): Promise<CalendarEvent[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      console.log(`📅 CALENDAR: Fetching events between ${startDate} and ${endDate}`);
      
      const { data, error } = await supabase
        .rpc('get_booking_calendar_events', {
          start_date: startDate || null,
          end_date: endDate || null
        });
      
      if (error) {
        throw error;
      }

      // Process events and handle multi-day bookings correctly
      const events: CalendarEvent[] = [];
      
      for (const event of data || []) {
        // Get all dates for this booking
        const bookingDates = await this.getBookingDates(event.booking_id);
        const mainDate = event.course_date;
        
        // Combine and deduplicate dates
        const allDates = mainDate ? [mainDate, ...bookingDates] : bookingDates;
        const uniqueDates = [...new Set(allDates)].sort();
        
        console.log(`📅 CALENDAR: Booking ${event.booking_id} dates:`, {
          mainDate,
          additionalDates: bookingDates,
          uniqueDates
        });
        
        // Create one calendar event per unique date
        uniqueDates.forEach(dateStr => {
          events.push({
            id: `${event.booking_id}-${dateStr}`,
            title: `${event.company_name} - ${event.course_name}`,
            date: new Date(dateStr + 'T12:00:00'),
            startTime: event.start_time,
            endTime: event.end_time,
            type: 'booking' as const,
            companyName: event.company_name,
            courseName: event.course_name,
            attendeeCount: event.attendee_count,
            status: event.status,
            resource: {
              customerId: event.customer_id,
              bookingId: event.booking_id,
              instructorId: event.instructor_id,
              locationName: event.location_address,
              contactName: event.contact_name,
              contactEmail: event.contact_email,
              contactPhone: event.contact_phone,
              notes: event.notes,
              customerPrice: event.total_price,
              instructorName: event.instructor_name,
              instructorEmail: event.instructor_email,
              instructorPrice: event.instructor_price,
              instructorPO: event.instructor_po,
              clientPO: event.client_po
            }
          });
        });
      }
      
      console.log(`📅 CALENDAR: Generated ${events.length} calendar events`);
      return events;
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      return [];
    }
  },

  async getBookingDatesOld(bookingId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('booking_dates')
      .select('date')
      .eq('booking_id', bookingId)
      .order('date');
    
    if (error) throw error;
    return (data || []).map(item => item.date);
  },

  async addBookingDates(bookingId: string, dates: string[]): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    if (dates.length === 0) return;

    try {
      console.log(`📅 BOOKING DATES: Adding ${dates.length} dates for booking ${bookingId}:`, dates);

      const bookingDates = dates.map(date => ({
        booking_id: bookingId,
        date: date
      }));

      const { error } = await supabase
        .from('booking_dates')
        .insert(bookingDates);
      
      if (error) {
        throw error;
      }
      
      console.log(`📅 BOOKING DATES: Successfully added ${dates.length} dates`);
    } catch (error) {
      console.error('Error adding booking dates:', error);
      throw error;
    }
  },

  async deleteBookingDates(bookingId: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      console.log(`📅 BOOKING DATES: Deleting dates for booking ${bookingId}`);
      const { error } = await supabase
        .from('booking_dates')
        .delete()
        .eq('booking_id', bookingId);
      
      if (error) {
        throw error;
      }
      
      console.log(`📅 BOOKING DATES: Successfully deleted dates for booking ${bookingId}`);
    } catch (error) {
      console.error('Error deleting booking dates:', error);
      throw error;
    }
  },

  async updateBookingDates(bookingId: string, dates: string[]): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      console.log(`📅 BOOKING DATES: Updating dates for booking ${bookingId}:`, dates);
      
      // First, delete existing booking dates
      await this.deleteBookingDates(bookingId);
      
      // Filter out any empty/invalid dates and ensure uniqueness
      const validDates = [...new Set(dates.filter(date => date && date.trim()))];
      
      // Add new dates only if we have valid dates
      if (validDates.length > 0) {
        await this.addBookingDates(bookingId, validDates);
        console.log(`📅 BOOKING DATES: Successfully updated with ${validDates.length} dates`);
      } else {
        console.log(`📅 BOOKING DATES: No additional dates to store`);
      }
    } catch (error) {
      console.error('Error updating booking dates:', error);
      throw error;
    }
  },

  async addBookingDatesWithTimes(bookingId: string, datesWithTimes: Array<{date: string, startTime?: string, endTime?: string}>): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    if (datesWithTimes.length === 0) return;

    try {
      console.log(`📅 BOOKING DATES WITH TIMES: Adding dates and times for booking ${bookingId}:`, datesWithTimes);
      
      // Filter out any empty/invalid dates
      const validDatesWithTimes = datesWithTimes.filter(item => item.date && item.date.trim());
      
      // Add new dates with times only if we have valid dates
      if (validDatesWithTimes.length > 0) {
        const datesToInsert = validDatesWithTimes.map(item => ({
          booking_id: bookingId,
          date: item.date,
          start_time: item.startTime || '09:00',
          end_time: item.endTime || null
        }));

        const { error } = await supabase
          .from('booking_dates')
          .insert(datesToInsert);
        
        if (error) {
          throw error;
        }
        
        console.log(`📅 BOOKING DATES WITH TIMES: Successfully added ${validDatesWithTimes.length} dates`);
      } else {
        console.log(`📅 BOOKING DATES WITH TIMES: No valid dates to add`);
      }
    } catch (error) {
      console.error('Error adding booking dates with times:', error);
      throw error;
    }
  },

  async updateBookingDatesWithTimes(bookingId: string, datesWithTimes: Array<{date: string, startTime: string, endTime: string}>): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      console.log(`📅 BOOKING DATES WITH TIMES: Updating dates and times for booking ${bookingId}:`, datesWithTimes);
      
      // First, delete existing booking dates
      await this.deleteBookingDates(bookingId);
      
      // Filter out any empty/invalid dates and ensure uniqueness
      const validDatesWithTimes = datesWithTimes.filter(item => item.date && item.date.trim());
      
      // Add new dates with times only if we have valid dates
      if (validDatesWithTimes.length > 0) {
        const datesToInsert = validDatesWithTimes.map(item => ({
          booking_id: bookingId,
          date: item.date,
          start_time: item.startTime || '09:00',
          end_time: item.endTime || null
        }));

        const { error } = await supabase
          .from('booking_dates')
          .insert(datesToInsert);
        
        if (error) {
          throw error;
        }
        
        console.log(`📅 BOOKING DATES WITH TIMES: Successfully updated with ${validDatesWithTimes.length} dates`);
      } else {
        console.log(`📅 BOOKING DATES WITH TIMES: No additional dates to store`);
      }
    } catch (error) {
      console.error('Error updating booking dates with times:', error);
      throw error;
    }
  }
};

// Booking Attendees Storage
export const supabaseBookingAttendeesStorage = {
  async getBookingAttendees(bookingId: string): Promise<Array<{id: string; attendeeName: string}>> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('booking_attendees')
        .select('id, attendee_name, booking_id')
        .eq('booking_id', bookingId)
        .order('created_at');
      
      if (error) {
        console.error('Error fetching booking attendees:', error);
        return [];
      }

      return (data || []).map(item => ({
        id: item.id,
        attendeeName: item.attendee_name
      }));
    } catch (error) {
      console.error('Error fetching booking attendees:', error);
      return [];
    }
  },

  // Batch fetch attendees for multiple bookings - reduces N+1 queries
  async getBookingAttendeesBatch(bookingIds: string[]): Promise<Map<string, Array<{id: string; attendeeName: string}>>> {
    if (!isSupabaseConfigured() || bookingIds.length === 0) {
      return new Map();
    }

    try {
      // Batch requests to avoid URL length limits (max ~50 IDs per request)
      const BATCH_SIZE = 50;
      const attendeesMap = new Map<string, Array<{id: string; attendeeName: string}>>();
      
      // Initialize map with all booking IDs (empty arrays)
      bookingIds.forEach(id => {
        attendeesMap.set(id, []);
      });

      // Process in batches
      for (let i = 0; i < bookingIds.length; i += BATCH_SIZE) {
        const batch = bookingIds.slice(i, i + BATCH_SIZE);
        
        const { data, error } = await supabase
          .from('booking_attendees')
          .select('id, attendee_name, booking_id')
          .in('booking_id', batch)
          .order('created_at');
        
        if (error) {
          console.error(`Error fetching booking attendees batch (${i}-${i + batch.length}):`, error);
          continue; // Continue with next batch
        }

        // Group attendees by booking_id
        (data || []).forEach(item => {
          const bookingId = item.booking_id;
          if (!attendeesMap.has(bookingId)) {
            attendeesMap.set(bookingId, []);
          }
          attendeesMap.get(bookingId)!.push({
            id: item.id,
            attendeeName: item.attendee_name
          });
        });
      }

      return attendeesMap;
    } catch (error) {
      console.error('Error fetching booking attendees batch:', error);
      return new Map();
    }
  },

  async addBookingAttendees(bookingId: string, attendeeNames: string[]): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    if (attendeeNames.length === 0) return;

    try {
      const attendees = attendeeNames
        .filter(name => name && name.trim())
        .map(attendeeName => ({
          booking_id: bookingId,
          attendee_name: attendeeName.trim()
        }));

      if (attendees.length === 0) return;

      const { error } = await supabase
        .from('booking_attendees')
        .insert(attendees);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error adding booking attendees:', error);
      throw error;
    }
  },

  async deleteBookingAttendees(bookingId: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      const { error } = await supabase
        .from('booking_attendees')
        .delete()
        .eq('booking_id', bookingId);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error deleting booking attendees:', error);
      throw error;
    }
  },

  async updateBookingAttendees(bookingId: string, attendeeNames: string[]): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      // First, delete existing attendees
      await this.deleteBookingAttendees(bookingId);
      
      // Filter out any empty/invalid names and add new attendees
      const validNames = attendeeNames.filter(name => name && name.trim());
      
      if (validNames.length > 0) {
        await this.addBookingAttendees(bookingId, validNames);
      }
    } catch (error) {
      console.error('Error updating booking attendees:', error);
      throw error;
    }
  }
};

// Separate Calendar Storage for better organization
export const supabaseCalendarStorage = {
  async getCalendarEvents(startDate?: string, endDate?: string): Promise<CalendarEvent[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      console.log(`📅 CALENDAR: Fetching events between ${startDate} and ${endDate}`);
      
      const { data, error } = await supabase
        .rpc('get_booking_calendar_events', {
          start_date: startDate || null,
          end_date: endDate || null
        });
      
      if (error) {
        throw error;
      }

      // Process events and handle multi-day bookings correctly
      const events: CalendarEvent[] = [];
      
      for (const event of data || []) {
        // Get all dates with times for this booking
        const bookingDatesWithTimes = await supabaseBookingDatesStorage.getBookingDatesWithTimes(event.booking_id);
        const mainDate = event.course_date;
        const mainStartTime = event.start_time || '09:00';
        const mainEndTime = event.end_time || '';
        
        // Create main date entry
        const mainDateEntry = mainDate ? [{
          date: mainDate,
          startTime: mainStartTime,
          endTime: mainEndTime
        }] : [];
        
        // Combine and deduplicate by date
        const allEntries = [...mainDateEntry, ...bookingDatesWithTimes];
        const uniqueEntries = allEntries.filter((entry, index, arr) => 
          arr.findIndex(e => e.date === entry.date) === index
        );
        
        console.log(`📅 CALENDAR: Booking ${event.booking_id} dates with times:`, {
          mainDate,
          mainStartTime,
          mainEndTime,
          additionalDates: bookingDatesWithTimes,
          uniqueEntries
        });
        
        // Create one calendar event per unique date with correct times
        uniqueEntries.forEach(dateEntry => {
          events.push({
            id: `${event.booking_id}-${dateEntry.date}`,
            title: `${event.company_name} - ${event.course_name}`,
            date: new Date(dateEntry.date + 'T12:00:00'),
            startTime: dateEntry.startTime,
            endTime: dateEntry.endTime,
            type: 'booking' as const,
            companyName: event.company_name,
            courseName: event.course_name,
            attendeeCount: event.attendee_count,
            status: event.status,
            resource: {
              customerId: event.customer_id,
              bookingId: event.booking_id,
              instructorId: event.instructor_id,
              locationName: event.location_address,
              contactName: event.contact_name,
              contactEmail: event.contact_email,
              contactPhone: event.contact_phone,
              notes: event.notes,
              customerPrice: event.total_price,
              instructorName: event.instructor_name,
              instructorEmail: event.instructor_email,
              instructorPrice: event.instructor_price,
              instructorPO: event.instructor_po,
              clientPO: event.client_po
            }
          });
        });
      }
      
      console.log(`📅 CALENDAR: Generated ${events.length} calendar events`);
      return events;
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      return [];
    }
  }
};

// Public Booking Clients Storage
import { PublicBookingClient } from '../types/crm';

export const supabasePublicBookingClientsStorage = {
  async getPublicBookingClients(bookingId: string): Promise<PublicBookingClient[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('public_booking_clients')
        .select('*')
        .eq('booking_id', bookingId)
        .order('created_at');

      if (error) {
        // Check if it's a network error (transient) vs actual error
        const isNetworkError = error.message?.includes('Failed to fetch') || 
                              error.message?.includes('ERR_CONNECTION_CLOSED') ||
                              error.message?.includes('ERR_QUIC_PROTOCOL_ERROR') ||
                              error.code === 'PGRST116'; // Connection error code
        
        if (isNetworkError) {
          // Network errors are transient - log at debug level, not error
          console.debug('Network error fetching public booking clients (will retry):', bookingId);
        } else {
          // Actual errors should be logged
          console.error('Error fetching public booking clients:', error);
        }
        return [];
      }

      // Load attendees for each client
      const clientsWithAttendees = await Promise.all(
        (data || []).map(async (item) => {
          // Fetch attendees for this client
          const { data: attendeesData, error: attendeesError } = await supabase
            .from('public_booking_client_attendees')
            .select('attendee_name')
            .eq('public_booking_client_id', item.id)
            .order('created_at');

          const attendees = attendeesError || !attendeesData
            ? []
            : attendeesData.map((a: any) => a.attendee_name);

          return {
            id: item.id,
            customerId: item.customer_id,
            contactId: item.contact_id,
            clientPrice: parseFloat(item.client_price || '0'),
            clientPO: item.client_po,
            attendeeCount: item.attendee_count || 1,
            notes: item.notes,
            attendees: attendees
          };
        })
      );

      return clientsWithAttendees;
    } catch (error: any) {
      // Check if it's a network error (transient) vs actual error
      const isNetworkError = error?.message?.includes('Failed to fetch') || 
                              error?.message?.includes('ERR_CONNECTION_CLOSED') ||
                              error?.message?.includes('ERR_QUIC_PROTOCOL_ERROR') ||
                              error?.code === 'PGRST116';
      
      if (isNetworkError) {
        // Network errors are transient - log at debug level, not error
        console.debug('Network error fetching public booking clients (will retry):', bookingId);
      } else {
        // Actual errors should be logged
        console.error('Error fetching public booking clients:', error);
      }
      return [];
    }
  },

  // Batch fetch public booking clients for multiple bookings - reduces N+1 queries
  async getPublicBookingClientsBatch(bookingIds: string[]): Promise<Map<string, PublicBookingClient[]>> {
    if (!isSupabaseConfigured() || bookingIds.length === 0) {
      return new Map();
    }

    try {
      // Batch requests to avoid URL length limits (max ~50 IDs per request)
      const BATCH_SIZE = 50;
      const clientsMap = new Map<string, PublicBookingClient[]>();
      const allClients: any[] = [];
      
      // Initialize map with all booking IDs (empty arrays)
      bookingIds.forEach(id => {
        clientsMap.set(id, []);
      });

      // Process booking IDs in batches
      for (let i = 0; i < bookingIds.length; i += BATCH_SIZE) {
        const batch = bookingIds.slice(i, i + BATCH_SIZE);
        
        const { data, error } = await supabase
          .from('public_booking_clients')
          .select('*')
          .in('booking_id', batch)
          .order('created_at');

        if (error) {
          // Check if it's a network error (transient) vs actual error
          const isNetworkError = error.message?.includes('Failed to fetch') || 
                                error.message?.includes('ERR_CONNECTION_CLOSED') ||
                                error.message?.includes('ERR_QUIC_PROTOCOL_ERROR') ||
                                error.code === 'PGRST116';
          
          if (isNetworkError) {
            console.debug(`⚠️ Network error fetching public booking clients batch (${i}-${i + batch.length}), continuing`);
            continue; // Continue with next batch
          }
          
          console.error(`Error fetching public booking clients batch (${i}-${i + batch.length}):`, error);
          continue; // Continue with next batch
        }

        if (data && data.length > 0) {
          allClients.push(...data);
        }
      }

      if (allClients.length === 0) {
        return clientsMap; // Already initialized with empty arrays
      }

      // Fetch attendees for all clients in batches
      const clientIds = allClients.map((c: any) => c.id);
      const attendeesByClient = new Map<string, string[]>();
      
      for (let i = 0; i < clientIds.length; i += BATCH_SIZE) {
        const batch = clientIds.slice(i, i + BATCH_SIZE);
        
        const { data: attendeesData } = await supabase
          .from('public_booking_client_attendees')
          .select('public_booking_client_id, attendee_name')
          .in('public_booking_client_id', batch)
          .order('created_at');
        
        // Group attendees by client ID
        (attendeesData || []).forEach((a: any) => {
          const clientId = a.public_booking_client_id;
          if (!attendeesByClient.has(clientId)) {
            attendeesByClient.set(clientId, []);
          }
          attendeesByClient.get(clientId)!.push(a.attendee_name);
        });
      }

      // Group clients by booking_id
      allClients.forEach((item: any) => {
        const bookingId = item.booking_id;
        if (!clientsMap.has(bookingId)) {
          clientsMap.set(bookingId, []);
        }
        clientsMap.get(bookingId)!.push({
          id: item.id,
          customerId: item.customer_id,
          contactId: item.contact_id,
          clientPrice: parseFloat(item.client_price || '0'),
          clientPO: item.client_po,
          attendeeCount: item.attendee_count || 1,
          notes: item.notes,
          attendees: attendeesByClient.get(item.id) || []
        });
      });

      return clientsMap;
    } catch (error: any) {
      // Network errors are transient - return empty map
      const isNetworkError = error?.message?.includes('Failed to fetch') || 
                            error?.message?.includes('ERR_CONNECTION_CLOSED') ||
                            error?.message?.includes('ERR_QUIC_PROTOCOL_ERROR');
      if (isNetworkError) {
        return new Map();
      }
      console.error('Error fetching public booking clients batch:', error);
      return new Map();
    }
  },

  async addPublicBookingClients(bookingId: string, clients: PublicBookingClient[]): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    if (clients.length === 0) return;

    try {
      const clientsData = clients.map(client => ({
        booking_id: bookingId,
        customer_id: client.customerId,
        contact_id: client.contactId || null,
        client_price: client.clientPrice,
        client_po: client.clientPO || null,
        attendee_count: client.attendeeCount || 1,
        notes: client.notes || null
      }));

      const { data: insertedClients, error } = await supabase
        .from('public_booking_clients')
        .insert(clientsData)
        .select('id');

      if (error) {
        throw error;
      }

      // Add attendees for each client
      if (insertedClients && insertedClients.length > 0) {
        for (let i = 0; i < clients.length; i++) {
          const client = clients[i];
          const insertedClientId = insertedClients[i]?.id;
          
          if (insertedClientId && client.attendees && client.attendees.length > 0) {
            const validAttendees = client.attendees.filter(name => name && name.trim());
            if (validAttendees.length > 0) {
              const attendeesData = validAttendees.map(attendeeName => ({
                public_booking_client_id: insertedClientId,
                attendee_name: attendeeName.trim()
              }));

              const { error: attendeesError } = await supabase
                .from('public_booking_client_attendees')
                .insert(attendeesData);

              if (attendeesError) {
                console.error('Error adding attendees for client:', attendeesError);
                // Don't throw - continue with other clients
              }
            }
          }
        }
      }
      
      // Clear booking cache after adding public booking clients
      try {
        const { localBookingStorage } = await import('./localStorageCache');
        localBookingStorage.clear();
        console.log('🗑️ Cleared booking cache after adding public booking clients');
      } catch (cacheError) {
        console.warn('Failed to clear booking cache:', cacheError);
      }
    } catch (error) {
      console.error('Error adding public booking clients:', error);
      throw error;
    }
  },

  async updatePublicBookingClients(bookingId: string, clients: PublicBookingClient[]): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      // Get existing clients to preserve IDs if possible
      const existingClients = await this.getPublicBookingClients(bookingId);
      const existingClientMap = new Map(existingClients.map(c => [c.customerId, c]));

      // Delete existing clients (this will cascade delete attendees)
      await this.deletePublicBookingClients(bookingId);

      // Add updated clients with attendees
      if (clients.length > 0) {
        await this.addPublicBookingClients(bookingId, clients);
      }
    } catch (error) {
      console.error('Error updating public booking clients:', error);
      throw error;
    }
  },

  async updatePublicBookingClientAttendees(publicBookingClientId: string, attendeeNames: string[]): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      // Delete existing attendees
      const { error: deleteError } = await supabase
        .from('public_booking_client_attendees')
        .delete()
        .eq('public_booking_client_id', publicBookingClientId);

      if (deleteError) {
        throw deleteError;
      }

      // Add new attendees
      const validAttendees = attendeeNames.filter(name => name && name.trim());
      if (validAttendees.length > 0) {
        const attendeesData = validAttendees.map(attendeeName => ({
          public_booking_client_id: publicBookingClientId,
          attendee_name: attendeeName.trim()
        }));

        const { error: insertError } = await supabase
          .from('public_booking_client_attendees')
          .insert(attendeesData);

        if (insertError) {
          throw insertError;
        }
      }
    } catch (error) {
      console.error('Error updating public booking client attendees:', error);
      throw error;
    }
  },

  async deletePublicBookingClients(bookingId: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      const { error } = await supabase
        .from('public_booking_clients')
        .delete()
        .eq('booking_id', bookingId);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error deleting public booking clients:', error);
      throw error;
    }
  }
};

// Client Notes Storage
interface ClientNote {
  id: string;
  customer_id: string;
  note_text: string;
  created_at: string;
  user_id?: string;
  user_name?: string;
  is_pinned?: boolean;
}

export const supabaseClientNotesStorage = {
  async getClientNotes(customerId: string, limit?: number, offset?: number): Promise<ClientNote[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      let query = supabase
        .from('client_notes')
        .select('*')
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });

      if (offset !== undefined && limit !== undefined) {
        query = query.range(offset, offset + limit - 1);
      } else if (limit !== undefined) {
        query = query.limit(limit);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching client notes:', error);
        return [];
      }

      return (data || []).map((note: Record<string, unknown>) => ({
        id: note.id,
        customer_id: note.customer_id,
        note_text: String(note.note_text ?? note.note ?? ''),
        created_at: note.created_at,
        user_id: note.user_id,
        user_name: note.user_name,
        is_pinned: note.is_pinned || false
      }));
    } catch (error) {
      console.error('Error in getClientNotes:', error);
      return [];
    }
  },

  async getClientNotesCount(customerId: string): Promise<number> {
    if (!isSupabaseConfigured()) {
      return 0;
    }

    try {
      const { count, error } = await supabase
        .from('client_notes')
        .select('*', { count: 'exact', head: true })
        .eq('customer_id', customerId);

      if (error) {
        console.error('Error fetching client notes count:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error in getClientNotesCount:', error);
      return 0;
    }
  },

  async addClientNote(note: ClientNote): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      // Ensure required fields are present
      if (!note.customer_id || !note.note_text?.trim()) {
        throw new Error('Customer ID and note text are required');
      }

      console.log('📝 CLIENT NOTES: Adding note for customer:', note.customer_id);

      // Minimal columns: remote client_notes often lack user_id, user_name, is_pinned (schema drift).
      const author = note.user_name?.trim();
      const raw = note.note_text.trim();
      const note_text = author ? `[${author}] ${raw}` : raw;

      const base = {
        id: note.id,
        customer_id: note.customer_id,
        created_at: note.created_at
      };
      const attempts = [
        { ...base, note: note_text, note_text },
        { ...base, note_text },
        { ...base, note: note_text }
      ];

      for (let i = 0; i < attempts.length; i++) {
        const { error } = await supabase.from('client_notes').insert(attempts[i]);
        if (!error) {
          console.log('📝 CLIENT NOTES: Successfully added note:', base.id);
          return;
        }
        const msg = error.message || '';
        const retryable =
          error.code === 'PGRST204' ||
          (error.code === '23502' && msg.includes('column "note"'));
        if (!retryable) {
          console.error('Supabase error adding client note:', error);
          throw error;
        }
        if (i === attempts.length - 1) {
          console.error('Supabase error adding client note:', error);
          throw error;
        }
      }
    } catch (error) {
      console.error('Error in addClientNote:', error);
      throw error;
    }
  },

  async updateClientNote(note: ClientNote): Promise<void> {
    const { error } = await supabase
      .from('client_notes')
      .update(note)
      .eq('id', note.id);
    
    if (error) throw error;
  },

  async deleteClientNote(id: string): Promise<void> {
    const { error } = await supabase
      .from('client_notes')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// Stats Storage for Dashboard
export const supabaseStatsStorage = {
  async getDashboardStats(): Promise<{
    totalCustomers: number;
    totalCertificates: number;
    activeReminders: number;
    dueReviewReminders: number;
    totalLocations: number;
    totalContacts: number;
    unpaidCustomers: number;
    totalBookings: number;
    recentActivity: number;
  }> {
    try {
      const [customersCount, bookingsCount, locationsCount, contactsCount, certificatesCount] = await Promise.all([
        supabase.from('customers').select('id', { count: 'exact', head: true }),
        supabase.from('bookings').select('id', { count: 'exact', head: true }),
        supabase.from('locations').select('id', { count: 'exact', head: true }),
        supabase.from('contacts').select('id', { count: 'exact', head: true }),
        supabase.from('customers').select('generated_certificates').limit(1000),
      ]);

      if (customersCount.error) throw customersCount.error;
      if (bookingsCount.error) throw bookingsCount.error;
      if (locationsCount.error) throw locationsCount.error;
      if (contactsCount.error) throw contactsCount.error;
      if (certificatesCount.error) throw certificatesCount.error;

      const totalCustomers = customersCount.count || 0;
      const totalBookings = bookingsCount.count || 0;
      const totalLocations = locationsCount.count || 0;
      const totalContacts = contactsCount.count || 0;

      let totalCertificates = 0;
      (certificatesCount.data || []).forEach((customer: { generated_certificates?: unknown[] }) => {
        if (Array.isArray(customer.generated_certificates)) {
          totalCertificates += customer.generated_certificates.length;
        }
      });

      const remindersPrimary = await supabase
        .from('certificate_reminders')
        .select('expiry_date, status')
        .limit(1000);
      if (remindersPrimary.error) throw remindersPrimary.error;
      const remindersData = (remindersPrimary.data || []) as { expiry_date?: string; status?: string | null }[];

      const reminders = (remindersData || []).filter((r: any) => !['renewed', 'completed', 'cancelled'].includes(String(r?.status ?? '').toLowerCase()));
      const today = new Date().toISOString().split('T')[0];
      
      // Count distinct customers with outstanding balance from the financial view.
      // Falls back to 0 if the view is unavailable (older DBs) — never blocks the dashboard.
      let unpaidCustomers = 0;
      try {
        const { data: outstandingRows, error: outstandingErr } = await supabase
          .from('v_outstanding_balances')
          .select('individual_customer_id, company_customer_id, payer_customer_id');
        if (!outstandingErr && Array.isArray(outstandingRows)) {
          const ids = new Set<string>();
          for (const r of outstandingRows as Array<Record<string, unknown>>) {
            const payer = r.payer_customer_id as string | null | undefined;
            const indiv = r.individual_customer_id as string | null | undefined;
            const company = r.company_customer_id as string | null | undefined;
            const id = payer || indiv || company || null;
            if (id) ids.add(id);
          }
          unpaidCustomers = ids.size;
        }
      } catch (e) {
        console.warn('v_outstanding_balances unavailable (non-blocking):', e);
      }

      // Calculate expiring reminders (within 30 days)
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      const todayDate = new Date(today);
      
      const expiringReminders = reminders.filter(r => {
        // renewed / completed / cancelled rows are already excluded above via status
        if (!r.expiry_date) return false;
        const expStr = String(r.expiry_date);
        
        let expiryDate: Date;
        
        // Try parsing as ISO format first (YYYY-MM-DD)
        if (expStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          expiryDate = new Date(expStr);
          if (isNaN(expiryDate.getTime())) return false;
        } else {
          // Parse DD-MM-YYYY format (legacy)
          const parts = expStr.split('-');
          if (parts.length !== 3) return false;
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
          const year = parseInt(parts[2], 10);
          expiryDate = new Date(year, month, day);
          if (isNaN(expiryDate.getTime())) return false;
        }
        
        // Use consistent date comparison - only show certificates expiring within 30 days (not expired)
        return expiryDate >= todayDate && expiryDate <= thirtyDaysFromNow;
      });

      // Review reminders due — only fetch the columns used for filtering.
      let dueReviewReminders = 0;
      const reviewWide = await supabase
        .from('review_reminders')
        .select('status, sent_at')
        .limit(1000);
      if (!reviewWide.error && reviewWide.data) {
        dueReviewReminders = reviewWide.data.filter((r: any) => {
          if (r.sent_at) return false;
          const st = String(r.status ?? 'pending').toLowerCase();
          if (st !== 'pending') return false;
          return true;
        }).length;
      } else if (reviewWide.error) {
        console.warn('review_reminders dashboard:', reviewWide.error);
      }

      return {
        totalCustomers,
        totalCertificates,
        activeReminders: expiringReminders.length,
        dueReviewReminders,
        totalLocations,
        totalContacts,
        unpaidCustomers,
        totalBookings,
        recentActivity: 0
      };
    } catch (error) {
      console.error('Error getting dashboard stats:', error);
      return {
        totalCustomers: 0,
        totalCertificates: 0,
        activeReminders: 0,
        dueReviewReminders: 0,
        totalLocations: 0,
        totalContacts: 0,
        unpaidCustomers: 0,
        totalBookings: 0,
        recentActivity: 0
      };
    }
  },

  /**
   * Get stats for a time period and the previous period (for comparison/growth).
   * Used by dashboard analytics with Daily / Weekly / Monthly filters.
   */
  async getPeriodStats(period: 'day' | 'week' | 'month'): Promise<{
    periodLabel: string;
    current: {
      bookingsCreated: number;
      bookingsCompleted: number;
      customersAdded: number;
      contactsAdded: number;
      emailsSent: number;
    };
    previous: {
      bookingsCreated: number;
      bookingsCompleted: number;
      customersAdded: number;
      contactsAdded: number;
      emailsSent: number;
    };
  }> {
    const now = new Date();
    let currentStart: Date;
    let currentEnd: Date;
    let previousStart: Date;
    let previousEnd: Date;

    if (period === 'day') {
      currentStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      currentEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      previousStart = new Date(currentStart);
      previousStart.setDate(previousStart.getDate() - 1);
      previousEnd = new Date(previousStart.getFullYear(), previousStart.getMonth(), previousStart.getDate(), 23, 59, 59);
    } else if (period === 'week') {
      const dayOfWeek = now.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      currentStart = new Date(now);
      currentStart.setDate(now.getDate() + mondayOffset);
      currentStart.setHours(0, 0, 0, 0);
      currentEnd = new Date(currentStart);
      currentEnd.setDate(currentStart.getDate() + 6);
      currentEnd.setHours(23, 59, 59, 999);
      previousStart = new Date(currentStart);
      previousStart.setDate(previousStart.getDate() - 7);
      previousEnd = new Date(previousStart);
      previousEnd.setDate(previousStart.getDate() + 6);
      previousEnd.setHours(23, 59, 59, 999);
    } else {
      currentStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
      currentEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      previousStart = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0);
      previousEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
    }

    const toISO = (d: Date) => d.toISOString();
    const toDateOnly = (d: Date) => d.toISOString().split('T')[0];

    const runCounts = async (start: Date, end: Date) => {
      const startStr = toISO(start);
      const endStr = toISO(end);
      const startDateOnly = toDateOnly(start);
      const endDateOnly = toDateOnly(end);

      const [
        bookingsCreatedRes,
        bookingsCompletedRes,
        customersRes,
        contactsRes,
        emailsRes
      ] = await Promise.all([
        supabase.from('bookings').select('id', { count: 'exact', head: true }).gte('created_at', startStr).lte('created_at', endStr),
        supabase.from('bookings').select('id', { count: 'exact', head: true }).eq('status', 'completed').gte('course_date', startDateOnly).lte('course_date', endDateOnly),
        supabase.from('customers').select('id', { count: 'exact', head: true }).gte('created_at', startStr).lte('created_at', endStr),
        supabase.from('contacts').select('id', { count: 'exact', head: true }).gte('created_at', startStr).lte('created_at', endStr),
        supabase.from('email_outbox').select('id', { count: 'exact', head: true }).eq('status', 'sent').gte('created_at', startStr).lte('created_at', endStr)
      ]);

      return {
        bookingsCreated: bookingsCreatedRes.count ?? 0,
        bookingsCompleted: bookingsCompletedRes.count ?? 0,
        customersAdded: customersRes.count ?? 0,
        contactsAdded: contactsRes.count ?? 0,
        emailsSent: emailsRes.count ?? 0
      };
    };

    try {
      const [current, previous] = await Promise.all([
        runCounts(currentStart, currentEnd),
        runCounts(previousStart, previousEnd)
      ]);

      const periodLabels = { day: 'Today', week: 'This week', month: 'This month' };
      return {
        periodLabel: periodLabels[period],
        current,
        previous
      };
    } catch (error) {
      console.error('Error getting period stats:', error);
      return {
        periodLabel: period === 'day' ? 'Today' : period === 'week' ? 'This week' : 'This month',
        current: { bookingsCreated: 0, bookingsCompleted: 0, customersAdded: 0, contactsAdded: 0, emailsSent: 0 },
        previous: { bookingsCreated: 0, bookingsCompleted: 0, customersAdded: 0, contactsAdded: 0, emailsSent: 0 }
      };
    }
  },

  /**
   * Get stats for two date ranges (main period + optional comparison period).
   * Used by Reports tab. When comparisonRange is provided, both use explicit ranges; no auto "previous period".
   */
  async getPeriodStatsWithDateRange(
    startDate: string,
    endDate: string,
    comparisonRange?: { start: string; end: string } | null
  ): Promise<{
    current: {
      bookingsCreated: number;
      bookingsCompleted: number;
      customersAdded: number;
      contactsAdded: number;
      emailsSent: number;
    };
    previous?: {
      bookingsCreated: number;
      bookingsCompleted: number;
      customersAdded: number;
      contactsAdded: number;
      emailsSent: number;
    };
    previousRange?: { start: string; end: string };
  }> {
    const runCounts = async (start: string, end: string) => {
      const startDt = new Date(start + 'T00:00:00');
      const endDt = new Date(end + 'T23:59:59');
      const startStr = startDt.toISOString();
      const endStr = endDt.toISOString();

      const [
        bookingsCreatedRes,
        bookingsCompletedRes,
        customersRes,
        contactsRes,
        emailsRes
      ] = await Promise.all([
        supabase.from('bookings').select('id', { count: 'exact', head: true }).gte('created_at', startStr).lte('created_at', endStr),
        supabase.from('bookings').select('id', { count: 'exact', head: true }).eq('status', 'completed').gte('course_date', start).lte('course_date', end),
        supabase.from('customers').select('id', { count: 'exact', head: true }).gte('created_at', startStr).lte('created_at', endStr),
        supabase.from('contacts').select('id', { count: 'exact', head: true }).gte('created_at', startStr).lte('created_at', endStr),
        supabase.from('email_outbox').select('id', { count: 'exact', head: true }).eq('status', 'sent').gte('created_at', startStr).lte('created_at', endStr)
      ]);

      return {
        bookingsCreated: bookingsCreatedRes.count ?? 0,
        bookingsCompleted: bookingsCompletedRes.count ?? 0,
        customersAdded: customersRes.count ?? 0,
        contactsAdded: contactsRes.count ?? 0,
        emailsSent: emailsRes.count ?? 0
      };
    };

    try {
      const current = await runCounts(startDate, endDate);
      if (!comparisonRange || !comparisonRange.start || !comparisonRange.end) {
        return { current };
      }

      const previous = await runCounts(comparisonRange.start, comparisonRange.end);
      return {
        current,
        previous,
        previousRange: { start: comparisonRange.start, end: comparisonRange.end }
      };
    } catch (error) {
      console.error('Error getting period stats with date range:', error);
      return {
        current: { bookingsCreated: 0, bookingsCompleted: 0, customersAdded: 0, contactsAdded: 0, emailsSent: 0 }
      };
    }
  },

  async getComparisonStats(): Promise<{
    thisMonthBookings: number;
    lastMonthBookings: number;
    thisMonthClients: number;
    lastMonthClients: number;
    thisMonthRevenue: number;
    lastMonthRevenue: number;
  }> {
    try {
      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];

      const [thisMonthBookingsRes, lastMonthBookingsRes, thisMonthClientsRes, lastMonthClientsRes] = await Promise.all([
        supabase.from('bookings')
          .select('id', { count: 'exact', head: true })
          .gte('course_date', thisMonthStart)
          .lte('course_date', thisMonthEnd),
        supabase.from('bookings')
          .select('id', { count: 'exact', head: true })
          .gte('course_date', lastMonthStart)
          .lte('course_date', lastMonthEnd),
        supabase.from('customers')
          .select('id', { count: 'exact', head: true })
          .gte('created_at', thisMonthStart)
          .lte('created_at', thisMonthEnd),
        supabase.from('customers')
          .select('id', { count: 'exact', head: true })
          .gte('created_at', lastMonthStart)
          .lte('created_at', lastMonthEnd)
      ]);

      return {
        thisMonthBookings: thisMonthBookingsRes.count || 0,
        lastMonthBookings: lastMonthBookingsRes.count || 0,
        thisMonthClients: thisMonthClientsRes.count || 0,
        lastMonthClients: lastMonthClientsRes.count || 0,
        thisMonthRevenue: 0,
        lastMonthRevenue: 0
      };
    } catch (error) {
      console.error('Error getting comparison stats:', error);
      return {
        thisMonthBookings: 0,
        lastMonthBookings: 0,
        thisMonthClients: 0,
        lastMonthClients: 0,
        thisMonthRevenue: 0,
        lastMonthRevenue: 0
      };
    }
  },

  /**
   * Big customers report: aggregate bookings by customer in a date range.
   * Returns customer id, company name, booking count, and total revenue.
   * Excludes cancelled bookings and bookings without customer_id.
   */
  async getBigCustomersReport(params: {
    startDate: string;
    endDate: string;
  }): Promise<Array<{ customerId: string; companyName: string; bookingCount: number; totalRevenue: number }>> {
    if (!isSupabaseConfigured()) {
      return [];
    }
    try {
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('customer_id, payer_customer_id, payer_type, total_price')
        .or('customer_id.not.is.null,payer_customer_id.not.is.null')
        .neq('status', 'cancelled')
        .gte('course_date', params.startDate)
        .lte('course_date', params.endDate);

      if (bookingsError) throw bookingsError;
      const rows = bookings || [];

      const byCustomer = new Map<string, { count: number; revenue: number }>();
      for (const row of rows) {
        const id = row.payer_customer_id || row.customer_id;
        if (!id) continue;
        const price = typeof row.total_price === 'number' ? row.total_price : parseFloat(row.total_price) || 0;
        if (!byCustomer.has(id)) {
          byCustomer.set(id, { count: 0, revenue: 0 });
        }
        const agg = byCustomer.get(id)!;
        agg.count += 1;
        agg.revenue += price;
      }

      const customerIds = Array.from(byCustomer.keys());
      if (customerIds.length === 0) {
        return [];
      }

      const { data: customers, error: customersError } = await supabase
        .from('customers')
        .select('id, company_name, first_name, surname')
        .in('id', customerIds);

      if (customersError) throw customersError;
      const nameById = new Map<string, string>();
      (customers || []).forEach((c: any) => {
        const individualName = `${c.first_name || ''} ${c.surname || ''}`.trim();
        nameById.set(c.id, c.company_name || individualName || '—');
      });

      return customerIds.map((customerId) => {
        const agg = byCustomer.get(customerId)!;
        return {
          customerId,
          companyName: nameById.get(customerId) || '—',
          bookingCount: agg.count,
          totalRevenue: Math.round(agg.revenue * 100) / 100
        };
      });
    } catch (error) {
      console.error('Error getting big customers report:', error);
      return [];
    }
  },

  /** Bookings created in date range (by created_at). For activity report detail. */
  async getBookingsCreatedInRange(startDate: string, endDate: string): Promise<Array<{ id: string; courseName: string; courseDate: string; companyName: string; createdAt: string }>> {
    if (!isSupabaseConfigured()) return [];
    try {
      const start = new Date(startDate + 'T00:00:00').toISOString();
      const end = new Date(endDate + 'T23:59:59').toISOString();
      const { data: rows, error } = await supabase
        .from('bookings')
        .select('id, course_name, course_date, created_at, customer_id, payer_customer_id')
        .gte('created_at', start)
        .lte('created_at', end)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const bookings = rows || [];
      const customerIds = [...new Set(bookings.map((b: any) => b.payer_customer_id || b.customer_id).filter(Boolean))];
      const nameById = new Map<string, string>();
      if (customerIds.length > 0) {
        const { data: cust } = await supabase
          .from('customers')
          .select('id, company_name, first_name, surname')
          .in('id', customerIds);
        (cust || []).forEach((c: any) => {
          const individualName = `${c.first_name || ''} ${c.surname || ''}`.trim();
          nameById.set(c.id, c.company_name || individualName || '—');
        });
      }
      return bookings.map((b: any) => ({
        id: b.id,
        courseName: b.course_name || '—',
        courseDate: b.course_date || '—',
        companyName: (b.payer_customer_id || b.customer_id) ? (nameById.get(b.payer_customer_id || b.customer_id) || '—') : '—',
        createdAt: b.created_at || ''
      }));
    } catch (e) {
      console.error('getBookingsCreatedInRange:', e);
      return [];
    }
  },

  /** Bookings completed in date range (course_date in range, status completed). */
  async getBookingsCompletedInRange(startDate: string, endDate: string): Promise<Array<{ id: string; courseName: string; courseDate: string; companyName: string }>> {
    if (!isSupabaseConfigured()) return [];
    try {
      const { data: rows, error } = await supabase
        .from('bookings')
        .select('id, course_name, course_date, customer_id, payer_customer_id')
        .eq('status', 'completed')
        .gte('course_date', startDate)
        .lte('course_date', endDate)
        .order('course_date', { ascending: false });
      if (error) throw error;
      const bookings = rows || [];
      const customerIds = [...new Set(bookings.map((b: any) => b.payer_customer_id || b.customer_id).filter(Boolean))];
      const nameById = new Map<string, string>();
      if (customerIds.length > 0) {
        const { data: cust } = await supabase
          .from('customers')
          .select('id, company_name, first_name, surname')
          .in('id', customerIds);
        (cust || []).forEach((c: any) => {
          const individualName = `${c.first_name || ''} ${c.surname || ''}`.trim();
          nameById.set(c.id, c.company_name || individualName || '—');
        });
      }
      return bookings.map((b: any) => ({
        id: b.id,
        courseName: b.course_name || '—',
        courseDate: b.course_date || '—',
        companyName: (b.payer_customer_id || b.customer_id) ? (nameById.get(b.payer_customer_id || b.customer_id) || '—') : '—'
      }));
    } catch (e) {
      console.error('getBookingsCompletedInRange:', e);
      return [];
    }
  },

  /** Customers added in date range (created_at). */
  async getCustomersAddedInRange(startDate: string, endDate: string): Promise<Array<{ id: string; companyName: string; createdAt: string }>> {
    if (!isSupabaseConfigured()) return [];
    try {
      const start = new Date(startDate + 'T00:00:00').toISOString();
      const end = new Date(endDate + 'T23:59:59').toISOString();
      const { data: rows, error } = await supabase
        .from('customers')
        .select('id, company_name, created_at')
        .gte('created_at', start)
        .lte('created_at', end)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (rows || []).map((r: any) => ({
        id: r.id,
        companyName: r.company_name || '—',
        createdAt: r.created_at || ''
      }));
    } catch (e) {
      console.error('getCustomersAddedInRange:', e);
      return [];
    }
  },

  /** Contacts added in date range (created_at). */
  async getContactsAddedInRange(startDate: string, endDate: string): Promise<Array<{ id: string; firstName: string; lastName: string; email: string; companyName: string; createdAt: string }>> {
    if (!isSupabaseConfigured()) return [];
    try {
      const start = new Date(startDate + 'T00:00:00').toISOString();
      const end = new Date(endDate + 'T23:59:59').toISOString();
      const { data: rows, error } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, email, created_at, customer_id')
        .gte('created_at', start)
        .lte('created_at', end)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const contacts = rows || [];
      const customerIds = [...new Set(contacts.map((c: any) => c.customer_id).filter(Boolean))];
      const nameById = new Map<string, string>();
      if (customerIds.length > 0) {
        const { data: cust } = await supabase.from('customers').select('id, company_name').in('id', customerIds);
        (cust || []).forEach((c: any) => nameById.set(c.id, c.company_name || '—'));
      }
      return contacts.map((c: any) => ({
        id: c.id,
        firstName: c.first_name || '—',
        lastName: c.last_name || '—',
        email: c.email || '—',
        companyName: c.customer_id ? (nameById.get(c.customer_id) || '—') : '—',
        createdAt: c.created_at || ''
      }));
    } catch (e) {
      console.error('getContactsAddedInRange:', e);
      return [];
    }
  }
};

// Admin Tasks Storage
export const supabaseAdminTasksStorage = {
  async getTasks(): Promise<any[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('admin_tasks')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching admin tasks:', error);
      return [];
    }
  },

  async getPendingTasksCount(): Promise<number> {
    if (!isSupabaseConfigured()) {
      return 0;
    }

    try {
      const { data, error } = await supabase
        .from('admin_tasks')
        .select('completed')
        .eq('completed', false);

      if (error) throw error;
      return data?.length || 0;
    } catch (error: any) {
      console.error('Error fetching pending tasks count:', error);
      
      // Check if this is a network/service error (not auth error)
      const isNetworkError = error?.message?.includes('Failed to fetch') || 
                            error?.message?.includes('NetworkError') ||
                            error?.code === 'ECONNREFUSED' ||
                            error?.code === 'ETIMEDOUT';
      
      if (isNetworkError) {
        console.log('⚠️ Network error fetching pending tasks count - returning 0');
      }
      
      return 0;
    }
  },

  async addTask(task: any): Promise<any> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase not configured');
    }

    try {
      const { data, error } = await supabase
        .from('admin_tasks')
        .insert([task])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding admin task:', error);
      throw error;
    }
  },

  async updateTask(taskId: string, updates: any): Promise<any> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase not configured');
    }

    try {
      const { data, error } = await supabase
        .from('admin_tasks')
        .update(updates)
        .eq('id', taskId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating admin task:', error);
      throw error;
    }
  },

  async deleteTask(taskId: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase not configured');
    }

    try {
      const { error } = await supabase
        .from('admin_tasks')
        .delete()
        .eq('id', taskId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting admin task:', error);
      throw error;
    }
  }
};

// Tests (quiz) storage
export const supabaseTestStorage = {
  async getTests(opts?: { bookingId?: string; courseName?: string }): Promise<any[]> {
    if (!isSupabaseConfigured()) return [];
    try {
      let q = supabase.from('tests').select('*').order('created_at', { ascending: false });
      if (opts?.bookingId) q = q.eq('booking_id', opts.bookingId);
      if (opts?.courseName) q = q.eq('course_name', opts.courseName);
      const { data, error } = await q;
      if (error) throw error;
      return (data || []).map((r: any) => ({
        id: r.id,
        tenantId: r.tenant_id,
        title: r.title,
        description: r.description,
        token: r.token,
        bookingId: r.booking_id,
        courseName: r.course_name,
        passPct: r.pass_percentage != null ? r.pass_percentage : 80,
        createdAt: r.created_at,
        updatedAt: r.updated_at,
        createdBy: r.created_by
      }));
    } catch (e) {
      console.error('getTests error:', e);
      return [];
    }
  },

  async getTestById(id: string): Promise<any | null> {
    if (!isSupabaseConfigured()) return null;
    try {
      const { data, error } = await supabase.from('tests').select('*').eq('id', id).single();
      if (error || !data) return null;
      return {
        id: data.id,
        tenantId: data.tenant_id,
        title: data.title,
        description: data.description,
        token: data.token,
        bookingId: data.booking_id,
        courseName: data.course_name,
        passPct: data.pass_percentage != null ? data.pass_percentage : 80,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        createdBy: data.created_by
      };
    } catch (e) {
      console.error('getTestById error:', e);
      return null;
    }
  },

  async getTestByToken(token: string): Promise<any | null> {
    if (!isSupabaseConfigured()) return null;
    try {
      const { data, error } = await supabase.from('tests').select('*').eq('token', token).maybeSingle();
      if (error || !data) return null;
      return {
        id: data.id,
        tenantId: data.tenant_id,
        title: data.title,
        description: data.description,
        token: data.token,
        bookingId: data.booking_id,
        courseName: data.course_name,
        passPct: data.pass_percentage != null ? data.pass_percentage : 80,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        createdBy: data.created_by
      };
    } catch (e) {
      console.error('getTestByToken error:', e);
      return null;
    }
  },

  async createTest(payload: { title: string; description?: string; bookingId?: string; courseName?: string; createdBy?: string; passPct?: number }): Promise<any | null> {
    if (!isSupabaseConfigured()) return null;
    try {
      const token = crypto.randomUUID?.()?.replace(/-/g, '') ?? Array.from(crypto.getRandomValues(new Uint8Array(16))).map(b => b.toString(16).padStart(2, '0')).join('');
      const passPct = payload.passPct != null && payload.passPct >= 0 && payload.passPct <= 100 ? payload.passPct : 80;
      const { data, error } = await supabase
        .from('tests')
        .insert({
          title: payload.title,
          description: payload.description ?? null,
          token,
          booking_id: payload.bookingId ?? null,
          course_name: payload.courseName ?? null,
          pass_percentage: passPct,
          created_by: payload.createdBy ?? null
        })
        .select()
        .single();
      if (error) throw error;
      return data ? {
        id: data.id,
        tenantId: data.tenant_id,
        title: data.title,
        description: data.description,
        token: data.token,
        bookingId: data.booking_id,
        courseName: data.course_name,
        passPct: data.pass_percentage != null ? data.pass_percentage : 80,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        createdBy: data.created_by
      } : null;
    } catch (e) {
      console.error('createTest error:', e);
      return null;
    }
  },

  async updateTest(id: string, payload: { title?: string; description?: string; bookingId?: string | null; courseName?: string | null; passPct?: number | null }): Promise<void> {
    if (!isSupabaseConfigured()) return;
    const updates: any = { updated_at: new Date().toISOString() };
    if (payload.title !== undefined) updates.title = payload.title;
    if (payload.description !== undefined) updates.description = payload.description;
    if (payload.bookingId !== undefined) updates.booking_id = payload.bookingId;
    if (payload.courseName !== undefined) updates.course_name = payload.courseName;
    if (payload.passPct !== undefined && payload.passPct != null && payload.passPct >= 0 && payload.passPct <= 100) updates.pass_percentage = payload.passPct;
    const { error } = await supabase.from('tests').update(updates).eq('id', id);
    if (error) throw error;
  },

  async deleteTest(id: string): Promise<void> {
    if (!isSupabaseConfigured()) return;
    const { error } = await supabase.from('tests').delete().eq('id', id);
    if (error) throw error;
  },

  async getTestQuestions(testId: string): Promise<any[]> {
    if (!isSupabaseConfigured()) return [];
    const { data, error } = await supabase
      .from('test_questions')
      .select('*')
      .eq('test_id', testId)
      .order('order_index');
    if (error) throw error;
    const opts = ['A', 'B', 'C', 'D'] as const;
    return (data || []).map((r: any) => {
      const questionType = (r.question_type === 'true_false' ? 'true_false' : 'multiple_choice') as 'multiple_choice' | 'true_false';
      const correctOptionsRaw = r.correct_options;
      const correctOptionsArr = typeof correctOptionsRaw === 'string' && correctOptionsRaw.trim()
        ? (correctOptionsRaw.split(',').map((s: string) => s.trim().toUpperCase()).filter((x: string) => opts.includes(x as any)) as ('A' | 'B' | 'C' | 'D')[])
        : undefined;
      const correctOptionDb = r.correct_option || (correctOptionsArr?.[0] ?? 'A');
      const correctOption = questionType === 'true_false'
        ? (correctOptionDb === 'A' ? 'True' : 'False')
        : correctOptionDb;
      return {
        id: r.id,
        testId: r.test_id,
        questionText: r.question_text,
        optionA: r.option_a,
        optionB: r.option_b,
        optionC: r.option_c,
        optionD: r.option_d,
        questionType,
        correctOption,
        correctOptions: questionType === 'true_false' ? undefined : correctOptionsArr,
        orderIndex: r.order_index
      };
    });
  },

  async setTestQuestions(testId: string, questions: Array<{ questionText: string; optionA: string; optionB: string; optionC: string; optionD: string; questionType?: 'multiple_choice' | 'true_false'; correctOption?: 'A' | 'B' | 'C' | 'D' | 'True' | 'False'; correctOptions?: ('A' | 'B' | 'C' | 'D')[]; orderIndex: number }>): Promise<void> {
    if (!isSupabaseConfigured()) return;
    await supabase.from('test_questions').delete().eq('test_id', testId);
    if (questions.length === 0) return;
    const rows = questions.map((q, i) => {
      const questionType = q.questionType === 'true_false' ? 'true_false' : 'multiple_choice';
      const isTrueFalse = questionType === 'true_false';
      let option_a: string;
      let option_b: string;
      let option_c: string;
      let option_d: string;
      let correct_option: string;
      let correct_options: string | null;
      if (isTrueFalse) {
        option_a = 'True';
        option_b = 'False';
        option_c = '';
        option_d = '';
        correct_option = (q.correctOption === 'True' ? 'A' : 'B');
        correct_options = null;
      } else {
        const multi = q.correctOptions && q.correctOptions.length > 0 ? q.correctOptions : [q.correctOption ?? 'A'];
        correct_option = multi[0];
        correct_options = multi.length > 1 ? multi.join(',') : null;
        option_a = q.optionA;
        option_b = q.optionB;
        option_c = q.optionC;
        option_d = q.optionD;
      }
      return {
        test_id: testId,
        question_text: q.questionText,
        question_type: questionType,
        option_a,
        option_b,
        option_c,
        option_d,
        correct_option,
        correct_options,
        order_index: q.orderIndex ?? i
      };
    });
    const { error } = await supabase.from('test_questions').insert(rows);
    if (error) throw error;
  },

  async getTestAttempts(testId: string): Promise<any[]> {
    if (!isSupabaseConfigured()) return [];
    const { data, error } = await supabase
      .from('test_attempts')
      .select('*')
      .eq('test_id', testId)
      .order('submitted_at', { ascending: false });
    if (error) throw error;
    return (data || []).map((r: any) => ({
      id: r.id,
      testId: r.test_id,
      attendeeName: r.attendee_name,
      attendeeEmail: r.attendee_email,
      bookingAttendeeId: r.booking_attendee_id,
      score: r.score,
      totalQuestions: r.total_questions,
      answersJson: r.answers_json || {},
      answersOrdered: Array.isArray(r.answers_ordered) ? r.answers_ordered : [],
      submittedAt: r.submitted_at,
      practicalPassed: r.practical_passed === true
    }));
  },

  async updateTestAttemptPractical(attemptId: string, practicalPassed: boolean): Promise<void> {
    if (!isSupabaseConfigured()) return;
    const { error } = await supabase
      .from('test_attempts')
      .update({ practical_passed: practicalPassed })
      .eq('id', attemptId);
    if (error) throw error;
  }
};